"use client"

import React, { createContext, useContext, useEffect, useMemo, useState } from "react"

export type Lang = 'ES' | 'EN' | 'FR'

type Dictionary = Record<string, string>

type I18nContextValue = {
  lang: Lang
  setLang: (l: Lang) => void
  t: (key: string) => string
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

const DICTS: Record<Lang, Dictionary> = {
  ES: {
  calculator_title: "Calculadora de cambio de divisas",
  amount_label: "Cantidad",
  from_label: "De",
  tipo_cambio_conoce_mas: "Conoce más",
  lets_travel_children_corporate_travel: "Turismo Corporativo",
  // Terms / Privacy Notice
  Pagos_title: "Realiza tu pago en línea",
  // Pagos / Payment page
  "pagos.client_title": "Datos del cliente",
  "pagos.client_name_label": "Nombre completo",
  "pagos.client_email_label": "Correo",
  "pagos.client_phone_label": "Teléfono",
  "pagos.client_country_label": "País",
  "pagos.service_category_label": "Categoría del servicio",
  "pagos.service_select_label": "Servicio a pagar",
  "pagos.currency_label": "Moneda",
  "pagos.amount_label": "Monto (sin centavos)",
  "pagos.card_data_title": "Datos de la tarjeta",
  "pagos.card_holder_label": "Titular (igual que la tarjeta)",
  "pagos.card_number_label": "Número de tarjeta",
  "pagos.card_exp_month_label": "Mes expiración",
  "pagos.card_exp_year_label": "Año expiración",
  "pagos.card_cvv_label": "CVV",
  "pagos.note_currency_hint": "* Sugerimos la moneda según el país, pero puedes cambiarla antes de pagar.",
  "pagos.canada_surcharge_note": "* Se agrega un cargo del 4% (equivalente a {surcharge} {currency}) por procesamiento en Canadá.",
  "pagos.pay_button": "Pagar",
  "pagos.processing": "Procesando...",
  "pagos.privacy_notice_link": "Ver aviso de privacidad",
  "pagos.payment_error_title": "No se pudo procesar el pago",
  "pagos.close": "Cerrar",
  // Receipt / Pago confirmado
  "pagos.badge_receipt": "Comprobante digital",
  "pagos.confirmed_title": "Tu pago está confirmado",
  "pagos.no_recent_payment_title": "No encontramos un pago reciente",
  "pagos.confirmed_description": "Descarga tu ticket con los datos completos del cargo y comparte la confirmación con tu asesor Link.",
  "pagos.no_recent_payment_description": "La sesión actual no tiene un pago confirmado. Regresa al formulario para registrar uno nuevo.",
  "pagos.download_ticket": "Descargar ticket PDF",
  "pagos.another_payment": "Realizar otro pago",
  "pagos.total_amount_label": "Monto total",
  "pagos.processor_label": "Procesador",
  "pagos.tx_label": "Transacción",
  "pagos.date_label": "Fecha",
  "pagos.client_label": "Cliente",
  "pagos.email_label": "Correo",
  "pagos.service_label": "Servicio",
  "pagos.brand_label": "Marca",
  "pagos.country_label": "País",
  "pagos.in_process": "En proceso",
  "pagos.no_payment_heading": "No hay pago reciente",
  "pagos.no_payment_description": "No encontramos un pago confirmado para mostrar. Vuelve al formulario para intentarlo de nuevo.",
  "pagos.go_to_payments": "Ir a pagos",

  // PDF specific
  "pagos.pdf_title": "Comprobante de Pago",
  "pagos.pdf_subtitle": "Grupo LET | Operado por OpenPay",
  "pagos.pdf_amount_paid": "MONTO PAGADO",
  "pagos.pdf_status": "ESTADO",
  "pagos.pdf_transaction": "TRANSACCIÓN",
  "pagos.pdf_details": "DETALLES DEL PAGO",
  "pagos.pdf_observations": "Observaciones",
  "pagos.pdf_obs_line1": "Este comprobante confirma que el cargo fue procesado a través de OpenPay.",
  "pagos.pdf_obs_line2": "Conserva este documento para cualquier aclaración.",
  "pagos.pdf_contact": "Contacto: +52 722 235 2370 · WhatsApp +52 712 231 5859",
  "pagos.pdf_address_line1": "Dirección: Calle Andres Quintana Roo #529, Col. Francisco Murguia, El Ranchito,",
  "pagos.pdf_address_line2": "C.P. 50130, Toluca, Mexico",
  "pagos.pdf_logo_error_title": "ERROR: No se pudo cargar el logo SVG",
  "pagos.pdf_logo_error_details": "Verifica la consola del navegador para detalles.",
  "pagos.verifying_openpay": "Verificando con OpenPay, un momento...",
  "pagos.openpay_status_report": "Estado reportado por OpenPay: {status}. Si el cargo fue completado, contacta a soporte.",
  "pagos.openpay_verify_error": "No se pudo verificar el estado del pago.",
  "pagos.download_error": "No se pudo generar el ticket. Intenta de nuevo.",
  terms_toc_contents: "Contenido",
  terms_print: "Imprimir",
  terms_header_privacy_notice: "Aviso de Privacidad",
  terms_intro_sentence: "El sitio web grupo-let.com, operado por Link Education & Travel (LET), es responsable de la recolección, uso y protección de sus datos personales.",
  terms_section1_title: "Responsable de la protección de datos personales",
  terms_section1_body: "LET es responsable del tratamiento de los datos personales en relación con los servicios de trámites, educación y viajes internacionales ofrecidos en grupo-let.com.",
  terms_section2_title: "Recolección de datos personales",
  terms_section2_intro: "Podemos solicitar, según aplique, la siguiente documentación para procesar sus servicios:",
  terms_section2_item1: "Formularios del asesor educativo o del sitio web.",
  terms_section2_item2: "Comprobantes de pago y documentos relacionados con el servicio.",
  terms_section2_item3: "Información para verificar la identidad del solicitante.",
  terms_section2_item4: "Documentos de propiedad (si el trámite lo requiere).",
  terms_section2_item5: "Facturas para validar pagos y transacciones vinculadas.",
  terms_section3_title: "Uso de los datos personales",
  terms_section3_item1: "Verificar la identidad del solicitante.",
  terms_section3_item2: "Procesar solicitudes y gestionar trámites educativos y de viaje.",
  terms_section3_item3: "Administrar la relación con los usuarios y mantenerlos informados.",
  terms_section3_item4: "Realizar cobros y pagos relacionados con los servicios ofrecidos.",
  terms_section3_note_summary: "Nota sobre finalidades adicionales",
  terms_section3_note_body: "Si llegáramos a usar sus datos para fines distintos, lo informaremos y, cuando aplique, solicitaremos su consentimiento.",
  terms_section4_title: "Enlaces a terceros",
  terms_section4_body: "Este sitio puede contener enlaces a sitios de terceros. LET no es responsable de sus prácticas de privacidad ni de su contenido. Le sugerimos revisar sus políticas.",
  terms_section5_title: "Actualizaciones de la política",
  terms_section5_body: "Revisamos periódicamente nuestras prácticas y actualizamos esta política. Consúltela con regularidad para conocer cambios.",
  terms_section6_title: "Google Analytics y Remarketing",
  terms_section6_body_part1: "Este sitio utiliza Google Analytics y publicidad gráfica (Remarketing). Más información en",
  terms_section6_body_part2: "Puede gestionar sus preferencias de anuncios de Google.",
  terms_section7_title: "Preguntas y comentarios",
  terms_section7_intro: "Si tiene dudas sobre esta política o nuestras prácticas, contáctenos:",
  terms_section7_phone: "Teléfono",
  terms_section7_address: "Dirección",
  terms_section8_title: "Confidencialidad y seguridad",
  terms_section8_body: "LET mantiene la confidencialidad de los datos personales y aplica medidas razonables de seguridad para protegerlos de accesos no autorizados, uso indebido o divulgación.",
  terms_section9_title: "Pagos",
  terms_section9_body: "Los pagos serán procesados por la pasarela Openpay.",
  terms_last_update: "Última actualización:",
  terms_last_update_date: "15 de septiembre de 2025",
  terms_skip_to_content: "Saltar al contenido",
  terms_toc_label: "Tabla de contenidos",
  terms_anchor_aria: "Enlace permanente",
  terms_print_aria: "Imprimir",
  terms_back_to_top: "Volver arriba",
  // Admin menu and dashboard
  admin_dashboard: "Panel Admin",
  admin_choose_section: "Elige una sección:",
  admin_menu_divisas: "Divisas",
  admin_menu_blog_posts: "Blog - Posts",
  admin_menu_blog_articles: "Blog - Artículos",
  admin_menu_blog_trending: "Blog - Tendencias",
  admin_menu_testimonials: "Testimonios",
  admin_menu_popups: "Popups",
  admin_label: "Admin",
  admin_items: "elementos",
  admin_configurations: "configuraciones",
  admin_common_active: "Activo",
  admin_common_inactive: "Inactivo",
  admin_common_saved: "Guardado",
  admin_common_save_error: "Error al guardar",
  admin_common_delete_error: "Error al eliminar",
  admin_common_update: "Actualizar",
  admin_common_create: "Crear",
  admin_common_add: "Agregar",
  // Blog posts
  admin_blog_posts: "Entradas del Blog",
  admin_blog_no_posts: "Sin entradas.",
  admin_blog_select_new_or_edit_post: "Selecciona \"Nuevo\" o elige una entrada para editar.",
  admin_post_kind_blog: "Blog",
  admin_post_kind_noticias: "Noticias",
  admin_post_kind_webinars: "Webinars",
  // Promotions
  admin_promotions: "Promociones",
  admin_promos_manage_desc: "Gestiona promociones con seguridad por token",
  admin_promos_id_title_required: "ID y Título son obligatorios",
  admin_token_persistence: "Se guarda en cookie “admin_token” y localStorage.",
  admin_save_token: "Guardar token",
  admin_promos_description_ph: "Describe la promoción. Puedes usar formato HTML, negritas, listas, etc.",
  promos_filter_title: "Filtrar promociones",
  promos_filter_description: "Selecciona una categoría para ver solo las promociones relacionadas.",
  admin_field_subtitle: "Subtítulo",
  admin_field_price_original: "Precio Original",
  admin_field_price_final: "Precio Final",
  admin_field_currency: "Moneda",
  admin_field_discount: "% Descuento",
  admin_field_validity: "Vigencia",
  admin_field_badge: "Badge",
  admin_field_package_link: "Link Paquete",
  admin_field_whatsapp: "WhatsApp",
  admin_field_travel_dates: "Fechas de viaje",
  admin_promos_none: "Sin promociones",
  admin_promos_confirm_delete: "¿Eliminar promoción?",
  // Popup
  admin_popup_confirm_delete: "¿Eliminar este popup?",
  admin_field_cta_label: "CTA Label",
  admin_field_cta_link: "CTA Link",
  admin_field_allow_close: "Permitir cerrar",
  admin_field_paths: "Paths (separados por coma)",
  admin_field_paths_ph: "/, /blog, /divisas, /blog*",
  admin_field_start_iso: "Inicio (ISO)",
  admin_field_end_iso: "Fin (ISO)",
  admin_triggers: "Disparadores",
  admin_trigger_onload: "Al cargar",
  admin_trigger_exit_intent: "Exit intent",
  admin_field_delay_ms: "Delay (ms)",
  admin_field_scroll_percent: "Scroll (%)",
  admin_frequency: "Frecuencia",
  admin_frequency_once: "Una vez por sesión",
  admin_hide_preview: "Ocultar vista previa",
  admin_show_preview: "Ver vista previa",
  admin_more_info: "Más info",
  // Trending
  admin_blog_trending: "Tendencias del Blog",
  admin_trending_type_articulo: "Artículo",
  admin_trending_type_guia: "Guía",
  admin_trending_type_promocion: "Promoción",
  admin_trending_type_paquete: "Paquete",
  // Products admin
  admin_products: "Productos",
  admin_products_desc: "Configura el contenido de cada producto/servicio",
  admin_products_required: "Slug, título y descripción son obligatorios",
  admin_products_selected_service: "Servicio seleccionado",
  admin_products_subtitle_ph: "Subtítulo del producto/servicio. Puedes usar formato HTML.",
  admin_products_hero_images: "Imágenes del Hero",
  admin_products_no_images_hint: "Sin imágenes: se usarán las predeterminadas por servicio.",
  admin_products_related_packages: "Paquetes relacionados (tipo promociones)",
  admin_products_no_related: "Sin paquetes relacionados",
  admin_field_days: "Días",
  admin_field_days_ph: "Número de días",
  admin_field_nights: "Noches",
  admin_field_nights_ph: "Número de noches",
  admin_field_countries: "Países",
  admin_field_countries_ph: "Número de países",
  admin_field_cities: "Ciudades",
  admin_field_cities_ph: "Número de ciudades",
  admin_field_include_flight: "Incluye vuelo",
  admin_field_airline_logo_url: "URL logo aerolínea",
  admin_field_itinerary_pdf_url: "URL PDF itinerario",
  admin_field_taxes_label: "Etiqueta de impuestos",
  admin_products_form_url: "URL del formulario (iframe)",
  admin_products_form_url_ph: "https://form.jotform.com/tu-formulario",
  admin_products_form_url_hint: "Si lo proporcionas y es un servicio, reemplazaremos los botones finales por este iframe y el botón del héroe abrirá este formulario.",
  admin_products_reasons: "Razones (¿Por qué elegirnos?)",
  admin_products_reason_ph: "Describe esta razón. Puedes usar formato HTML.",
  admin_products_no_items: "Sin elementos",
  admin_products_steps: "Pasos (proceso)",
  admin_products_step_ph: "Describe este paso del proceso. Puedes usar formato HTML.",
  admin_products_hero_variant: "Variante de Hero",
  admin_products_faq_q: "Pregunta",
  admin_products_faq_a: "Respuesta",
  admin_products_faq_a_ph: "Respuesta detallada. Puedes usar HTML, listas, enlaces, etc.",
  admin_products_faq_cat_general: "General",
  admin_products_faq_cat_about: "Sobre Nosotros",
  admin_products_faq_cat_travel: "Viaje",
  admin_products_faq_cat_insurance: "Seguros",
  admin_products_faq_cat_stay: "Estancia",
  admin_products_faq_cat_docs: "Documentación y trámites",
  admin_products_updated: "Actualizado",
  admin_products_none: "Sin productos",
  admin_products_confirm_delete: "¿Eliminar producto?",
  // Rates
  admin_rates_title: "Divisas",
  admin_rates_subtitle: "Cargar tasas y ver histórico",
  admin_token_sent_header: "Se envía como encabezado x-admin-token en las acciones.",
  admin_rates_base: "Base",
  admin_rates_pair: "Par (histórico)",
  admin_rates_history: "Histórico",
  admin_rates_latest_records: "Últimos registros",
  admin_rates_save_btn: "Guardar tasas",
  admin_no_data: "Sin datos",
  // Jobs admin
  admin_jobs_title: "Admin Vacantes",
  admin_jobs_confirm_delete: "¿Eliminar vacante?",
  admin_job_open: "Vacante abierta",
  admin_job_closed: "CERRADA",
  admin_field_location: "Ubicación",
  admin_field_department: "Departamento",
  admin_field_experience: "Experiencia",
  admin_field_salary: "Sueldo",
  admin_field_image_url: "Imagen URL",
  admin_field_link: "Link (href)",
  admin_field_link_ph: "/blog/mi-articulo o https://...",
  admin_field_views: "Views",
  admin_field_description: "Descripción",
  admin_field_requirements_ph: "Requisitos (uno por línea)",
  admin_field_benefits_ph: "Beneficios (uno por línea)",
  // Login
  admin_login_invalid: "Credenciales inválidas",
  admin_login_too_many: "Demasiados intentos. Intenta más tarde.",
  admin_login_error: "Error de autenticación",
  admin_login_attempts_left: "Intentos restantes",
  admin_login_blocked_retry_in: "Bloqueado. Reintenta en",
  admin_login_enter: "Entrar",
  // Admin common
  admin_token_label: "Token admin",
  admin_token_placeholder: "Pega tu RATES_ADMIN_TOKEN",
  admin_common_new: "Nuevo",
  admin_common_edit: "Editar",
  admin_common_delete: "Eliminar",
  admin_common_cancel: "Cancelar",
  admin_common_save: "Guardar",
  admin_common_remove: "Quitar",
  admin_common_saving: "Guardando…",
  admin_common_reload: "Recargar",
  admin_common_refresh: "Refrescar",
  admin_common_view: "Ver",
  admin_common_no_results: "Sin resultados",
  admin_common_no_image: "Sin imagen",
  admin_common_no_title: "Sin título",
  admin_common_no_date: "Sin fecha",
  admin_common_no_message: "(sin mensaje)",
  admin_common_not_provided: "(no proporcionado)",
  admin_common_export_csv: "Exportar CSV",
  admin_common_unauthorized_or_error: "No autorizado o error. Verifica el token.",
  // Admin table labels
  admin_table_date: "Fecha",
  admin_table_vacancy: "Vacante",
  admin_table_name: "Nombre",
  admin_table_email: "Email",
  admin_table_whatsapp: "WhatsApp",
  admin_table_cv: "CV",
  admin_table_message: "Mensaje",
  admin_table_status: "Estatus",
  admin_table_actions: "Acciones",
  admin_open: "Abrir",
  admin_open_cv: "Ver CV",
  // Applications
  admin_applications_title: "Postulaciones",
  admin_applications_search_ph: "Buscar por nombre, email o vacante",
  admin_application_to: "Postulación a",
  admin_app_status_nuevo: "nuevo",
  admin_app_status_revisando: "revisando",
  admin_app_status_entrevista: "entrevista",
  admin_app_status_oferta: "oferta",
  admin_app_status_rechazado: "rechazado",
  admin_app_status_contratado: "contratado",
  // Testimonials
  admin_testimonials_label: "testimonios",
  admin_testimonial: "Testimonio",
  admin_testimonials_confirm_delete: "¿Eliminar este testimonio?",
  // Blog
  admin_blog_articles: "Artículos",
  admin_blog_write_here: "Escribe tu artículo aquí…",
  admin_blog_missing_title_slug: "Falta título o slug",
  admin_editor: "Editor",
  admin_editing: "Editando",
  admin_blog_select_new_or_edit: "Selecciona \"Nuevo\" o elige un artículo para editar.",
  admin_blog_view_article: "Ver artículo",
  admin_common_no_articles: "Sin artículos.",
  // Fields
  admin_field_title: "Título",
  admin_field_slug: "Slug",
  admin_field_slug_ph: "mi-articulo",
  admin_field_excerpt: "Extracto",
  admin_field_category: "Categoría",
  admin_field_tags: "Tags (separados por coma)",
  admin_field_author: "Autor",
  admin_field_date: "Fecha",
  admin_field_read_time: "Tiempo de lectura",
  admin_field_service: "Servicio",
  admin_field_cover_image: "Imagen de portada (URL)",
  admin_field_content: "Contenido",
  admin_field_css: "CSS (opcional)",
  admin_field_css_ph: "/* Estilos personalizados para este artículo */",
  admin_field_js: "JavaScript (opcional)",
  admin_field_js_ph: "// Código JS para interactividad en este artículo",
  admin_field_video_url: "Video URL (YouTube o similar)",
  admin_field_thumbnail: "Miniatura (URL opcional)",
  admin_field_subtitle_optional: "Subtítulo (opcional)",
  admin_field_currency_hint: "Moneda (MXN, USD)",
  admin_field_price_optional: "Precio (opcional)",
  admin_field_price_prefix: "Etiqueta antes del precio",
  admin_field_price_suffix: "Etiqueta después del precio",
  admin_field_whatsapp_link: "Link WhatsApp",
  // Preview
  admin_preview: "Vista previa",
  admin_nothing_to_preview: "Nada para previsualizar.",
  // Rich Text Editor
  admin_rte_placeholder: "Escribe tu contenido aquí...",
  admin_rte_edit: "Editar",
  admin_rte_preview: "Vista Previa",
  admin_rte_helper_html: "Puedes usar HTML: <strong>negrita</strong>, <em>cursiva</em>, <h1>encabezados</h1>, <ul><li>listas</li></ul>, etc.",
  admin_rte_heading_1: "Encabezado 1",
  admin_rte_heading_2: "Encabezado 2",
  admin_rte_heading_3: "Encabezado 3",
  admin_rte_bold_title: "Negrita",
  admin_rte_italic_title: "Cursiva",
  admin_rte_underline_title: "Subrayado",
  admin_rte_bulleted_title: "Lista con viñetas",
  admin_rte_numbered_title: "Lista numerada",
  admin_rte_link_title: "Enlace",
  admin_rte_linebreak_title: "Salto de línea",
    nav_nosotros: "NOSOTROS",
    nav_destinos: "DESTINOS",
    nav_servicios: "SERVICIOS",
    nav_recursos: "RECURSOS",
    nav_eventos: "EVENTOS",
    nav_consulta: "Asesoría gratuita",
    nav_promociones: "PROMOCIONES",
    submenu_historia: "Conoce nuestra historia",
    submenu_destinos_desc: "Países y ciudades disponibles",
    submenu_servicios_desc: "Nuestros servicios",
    nav_lets_travel_desc : "Experiencias de viaje",
    nav_participations_desc : "Alianzas y lo mas nuevo",
    lets_inmigrate_children_canada: "Migración a Canadá",
    lets_travel_children_eta_uk: "eTA UK",
    lets_travel_children_eta_canadiense: "eTA Canadiense",
    lets_travel_children_other: "Otros tipos de visa",
    submenu_recursos_desc: "Guías y herramientas",
    submenu_eventos_desc: "Webinars y ferias",
    popular: "Popular",
    nuevo: "Nuevo",
  llamar_ahora: "LLAMAR AHORA",
  cta_formulario: "REGISTRATE ",
  // Route-specific CTA labels (used by CTASection to change button text per pathname)
  cta_formulario_home: "REGÍSTRATE",
  cta_enroll_now: "Inscríbete ahora",
  price_from_label: "Desde",
  form_register_title: "Formulario de registro",
  cta_formulario_divisas: "CONSULTAR TIPO DE CAMBIO",
  cta_formulario_study: "ESTOY INTERESADO EN ESTUDIAR",
    cta_titulo_1: "¿LISTO PARA COMENZAR TU",
    cta_titulo_resaltado: "AVENTURA?",
    cta_parrafo: "Únete a miles de personas que ya han transformado sus vidas con nuestros programas",
    cta_contactar: "CONTACTAR ASESOR",
    footer_estudios: "Estudios en el extranjero",
    footer_tramites: "Trámites migratorios y visas",
    footer_viajes: "Viajes turísticos",
    footer_grupos: "Grupos educativos internacionales",
    footer_nosotros: "Nosotros",
    footer_comunidad: "Comunidad",
    footer_tipo_cambio: "Tipo de cambio",
    footer_sobre_nosotros: "Sobre nosotros",
    footer_sucursales: "Nuestras sucursales",
    footer_bolsa: "Bolsa de trabajo",
    footer_blogs: "Blogs",
    footer_podcast: "Podcast",
    footer_acceso_padres: "Acceso a padres",
    footer_promociones: "Promociones",
    footer_viajes_internacionales: "Viajes internacionales",
    footer_viajes_nacionales: "Viajes nacionales",
    footer_despedida: "Despedida de solteros",
    footer_lunas_miel: "Lunas de miel",
    footer_cruceros: "Cruceros",
    footer_quinceaneras: "Quinceañeras",
    footer_idiomas: "Idiomas",
    footer_estudia_trabaja: "Estudia y trabaja",
    footer_educacion_superior: "Educación superior",
    footer_highschool: "Highschool",
    footer_aupair: "Au Pair",
    footer_pathway: "Pathway",
    footer_ingles_online: "Inglés online",
    footer_americana: "Americana",
    footer_canada: "Canadá",
    footer_toronto: "Toronto",
    footer_quebec: "Quebec",
    footer_londres: "Londres",
    footer_dublin: "Dublín",
    footer_vancouver: "Vancouver",
    footer_avisopriv: "Aviso de privacidad",
    footer_terminos: "Términos y condiciones",
    footer_arriba: "↑ Arriba",
    footer_consultar: "Consultar >>",
    footer_copy: "Todos los derechos reservados.",
    hero_title_1: "SOMOS TU FORMA DE",
    hero_title_2: "LLEGAR AL MUNDO",
    hero_desc: "Somos una empresa mexicana – canadiense, especializada en el turismo educativo y turismo de placer. Fundada en la ciudad de Toluca, en el año 2005. Hemos sido parte de más de 15,347 experiencias y sueños cumplidos, manteniendo la calidad y profesionalismo en nuestros servicios.",
    hero_hashtag: "#ViveAprendeCrece",
  home_intro_video_alt: "Experiencias LET en acción",
  podcast_hero_title: "UN PODCAST PENSADO EN TI",
  podcast_recent_episodes: "Episodios recientes",
  podcast_brand_label: "LET'S TALK",
  podcast_stats_error: "No se pudieron cargar las estadísticas del canal",
  podcast_impact_title: "Impacto del Podcast",
  podcast_impact_subtitle: "Métricas en tiempo real del canal de YouTube",
  podcast_metric_videos: "Videos del canal",
  podcast_metric_subscribers: "Nuestros suscriptores",
  podcast_metric_views: "Vistas del canal",
  podcast_why_title: "¿Por qué escucharnos?",
  podcast_why_subtitle: "Contenido curado para ayudarte a tomar decisiones claras y avanzar sin ruido",
  podcast_why_item1_title: "Producción Profesional",
  podcast_why_item1_desc: "Audio claro, estructura efectiva y edición enfocada en valor práctico, no relleno.",
  podcast_why_item1_badge: "Calidad Sonora",
  podcast_why_item2_title: "Contenido Verificado",
  podcast_why_item2_desc: "Información actualizada sobre becas, visas, procesos y oportunidades con fuentes confiables.",
  podcast_why_item2_badge: "Riguroso",
  podcast_why_item3_title: "Historias Reales",
  podcast_why_item3_desc: "Testimonios auténticos de estudiantes que transformaron su vida internacional.",
  podcast_why_item3_badge: "Inspiración",
  podcast_why_item4_title: "Enfoque Accionable",
  podcast_why_item4_desc: "Cada episodio termina con pasos concretos para avanzar inmediatamente.",
  podcast_why_item4_badge: "Aplicable",
  podcast_faq_q1: "¿Cada cuánto lanzan nuevos episodios?",
  podcast_faq_a1: "Publicamos un episodio principal cada martes y episodios breves extra algunos jueves con actualizaciones o tips rápidos.",
  podcast_faq_a2: "Puedes sugerir un tema o proponer un invitado enviando un mensaje por WhatsApp o usando nuestro formulario de contacto; presiona 'Proponer tema' para abrir el chat.",
  podcast_faq_a3: "Sí. Si quieres ser invitado, envíanos una breve presentación y el motivo a través de 'Proponer tema' y nuestro equipo te contactará.",
  podcast_faq_a4: "Los recursos mencionados se encuentran en la descripción de cada episodio en YouTube y en la página del episodio. Si necesitas los enlaces, solicítalos por el chat.",
  podcast_faq_a5: "No, el podcast es totalmente gratuito.",
  podcast_faq_q2: "¿Cómo sugiero un tema o invito a alguien?",
  podcast_faq_q3: "¿Puedo aparecer como invitado?",
  podcast_faq_q4: "¿Dónde accedo a recursos mencionados?",
  podcast_faq_q5: "¿El podcast tiene costo?",
  podcast_any_other_question: "¿Otra duda?",
  podcast_write_to_us_cta: "Escríbenos y la convertimos en episodio",
  podcast_suggest_topic: "PROPONER TEMA",
  // Tipo de cambio (divisas)
  tipo_cambio_title_prefix: "TIPO DE CAMBIO",
  tipo_cambio_title_highlight: "HOY",
  tabs_recomendaciones: "Recomendaciones",
  tabs_facturacion: "Facturación",
  factura_electronica_title: "Factura electrónica",
  factura_electronica_paragraph: "Solicita tu factura electrónica de forma rápida y segura.",
  factura_solicitar: "Solicitar factura",
  need_help_title: "¿Necesitas ayuda?",
  copy_result: "Copiar resultado",
  // JoinChat / WhatsApp messages
  joinchat_label_open: "Abrir chat",
  joinchat_pill_text: "¿Tienes dudas?",
  joinchat_root_message: "Hola Grupo LET 👋 Estoy en su sitio y me gustaría más información. Vengo de su página web.",
  joinchat_podcast_message: "Hola 👋 Escuché su podcast y tengo algunas dudas. Vengo de su página web.",
  joinchat_promotions_message: "Hola 👋 Vi sus promociones y me interesa saber más detalles. Vengo de su página web.",
  joinchat_category_study_message: "Hola 👋 Me interesa estudiar en el extranjero y quiero asesoría. Vengo de su página web.",
  joinchat_category_travel_message: "Hola 👋 Quiero información sobre viajes y paquetes. Vengo de su página web.",
  joinchat_tipo_cambio_message: "Hola 👋 Tengo dudas sobre el tipo de cambio, ¿me pueden ayudar? Vengo de su página web.",
  // Service-specific JoinChat messages
  joinchat_service_idiomas_message: "Hola 👋 Estoy interesado en los cursos de idiomas, ¿me pueden dar más información? Vengo de su página web.",
  "joinchat_service_work-and-study_message": "Hola 👋 Me interesa el programa de estudia y trabaja, ¿cómo funciona? Vengo de su página web.",
  "joinchat_service_educacion-superior_message": "Hola 👋 Quisiera información sobre educación superior y opciones de universidades. Vengo de su página web.",
  joinchat_service_highschool_message: "Hola 👋 Quiero saber más sobre los programas de High School. Vengo de su página web.",
  joinchat_service_aupair_message: "Hola 👋 Estoy interesado en el programa Au Pair, ¿qué requisitos hay? Vengo de su página web.",
  joinchat_service_nacionales_message: "Hola 👋 Quisiera información sobre viajes nacionales y sus paquetes. Vengo de su página web.",
  joinchat_service_internacionales_message: "Hola 👋 Estoy interesado en viajes internacionales, ¿qué destinos ofrecen? Vengo de su página web.",
  joinchat_service_quince_message: "Hola 👋 Quiero información sobre viajes para XV años. Vengo de su página web.",
  joinchat_service_despedidas_message: "Hola 👋 Me interesa organizar una despedida de solteros, ¿qué opciones tienen? Vengo de su página web.",
  joinchat_service_grupos_message: "Hola 👋 Necesito información sobre viajes en grupo y coordinación. Vengo de su página web.",
  joinchat_service_bodas_message: "Hola 👋 Quiero saber sobre destinos para bodas y paquetes disponibles. Vengo de su página web.",
  joinchat_service_personalizado_message: "Hola 👋 Me interesa un viaje personalizado; ¿cómo lo coordinan? Vengo de su página web.",
  // WhatsApp UI labels
  whatsapp_button: "WhatsApp",
  whatsapp_title: "WhatsApp",
  // small labels
  joinchat_close: "Cerrar",
    services_title: "NUESTROS SERVICIOS",
    services_subtitle: "Descubre las oportunidades que tenemos para ti. Cada programa está diseñado para brindarte una experiencia única y transformadora.",
  participaciones_hero_title: "Haz realidad tus sueños",
  participaciones_hero_subtitle: "Cumpliendo metas con LET",
  participaciones_experience_caption: "Más de 15 años de experiencia respaldando sueños internacionales",
  participaciones_stat_students: "Estudiantes Exitosos",
  participaciones_stat_countries: "Países de Destino",
  participaciones_stat_success: "Tasa de Éxito",
  participaciones_stat_institutions: "Instituciones",
  participaciones_process_title: "Tu proceso en 4 pasos",
  participaciones_process_subtitle: "Un proceso simple y transparente para hacer realidad tu sueño internacional",
  participaciones_step_1_title: "Consulta Gratuita",
  participaciones_step_1_desc: "Evaluamos tu perfil y definimos el mejor programa para ti",
  participaciones_step_2_title: "Aplicación",
  participaciones_step_2_desc: "Te ayudamos con toda la documentación y proceso de visa",
  participaciones_step_3_title: "Preparación",
  participaciones_step_3_desc: "Organizamos alojamiento, vuelos y todo lo necesario",
  participaciones_step_4_title: "Soporte en Destino",
  participaciones_step_4_desc: "Te acompañamos durante toda tu estadía",
  participaciones_faq_title: "Preguntas frecuentes",
  participaciones_faq_q1: "¿Qué servicios ofrece LET?",
  participaciones_faq_a1: "Ofrecemos servicios especializados en educación internacional, incluyendo asesoramiento educativo, gestión de visas, alojamiento y soporte completo durante tu experiencia en el extranjero.",
  participaciones_faq_q2: "¿Cuánto tiempo toma el proceso de visa de estudiante?",
  participaciones_faq_a2: "El tiempo varía según el país y el tipo de visa; normalmente el proceso puede tomar desde unas semanas hasta varios meses. Te recomendamos iniciar con tiempo y revisar los requisitos específicos del país de destino.",
  participaciones_faq_q3: "¿Puedo trabajar mientras estudio?",
  participaciones_faq_a3: "Depende de la visa y del país. Muchas visas de estudiante permiten trabajo a tiempo parcial; te asesoramos sobre las reglas y límites para que combines estudio y trabajo de forma legal.",
  participaciones_faq_q4: "¿Ofrecen seguimiento una vez que estoy en el destino?",
  participaciones_faq_a4: "Sí. Ofrecemos soporte continuo y seguimiento durante tu estancia, incluyendo ayuda con alojamiento, integración y cualquier incidencia que pueda surgir.",
    services_study_about: "Una oportunidad integral para desarrollar autonomía e independencia mientras perfeccionas tus habilidades académicas, emocionales y sociales.",
    services_travel_about: "Explora destinos fascinantes mientras aprendes y creces. Viajes educativos diseñados para expandir tu visión del mundo.",
    services_work_about: "Oportunidades laborales en Canadá que combinan trabajo y aprendizaje, desarrollando habilidades profesionales en un entorno internacional.",
    stats_experiencias: "Experiencias cumplidas",
    stats_anios: "Años de experiencia",
    stats_satisfaccion: "Satisfacción garantizada",
    testimonials_title: "TESTIMONIOS DE ÉXITO",
    testimonials_subtitle: "Conoce las historias reales de estudiantes que han transformado sus vidas con nuestros programas",
    testimonial_1_role: "Estudiante de Link Education",
    testimonial_1_content: "Grupo LET cambió mi vida completamente. El programa de estudios en Canadá me abrió puertas que nunca imaginé. El soporte fue increíble desde el primer día.",
    testimonial_2_role: "Participante Link Work",
    testimonial_2_content: "La experiencia de trabajar en Canadá mientras aprendía inglés fue invaluable. Ahora tengo un futuro profesional sólido y un nivel de inglés avanzado.",
    testimonial_3_role: "Viajera Link Travel",
    testimonial_3_content: "Los viajes educativos de LET son más que turismo. Aprendí tanto sobre la cultura canadiense mientras mejoraba mi inglés. ¡Experiencia inolvidable!",
    home_relevantes_title: "Nuestros Relevantes",
    home_relevantes_subtitle: "Lo más destacado para ti",
    home_intro_kicker: "QUIÉNES SOMOS",
    home_intro_title: "Link Education and Travel",
    home_intro_paragraph: "Desde el año 2005, nos hemos posicionado a nivel internacional como una empresa líder en México y Canadá. Estamos en constante expansión, consolidando alianzas globales y desarrollando proyectos que conectan culturas y oportunidades.",
    home_intro_point_1: "Desarrollamos programas que inspiran y transforman:",
    home_intro_point_2: "Campamentos de verano e invierno.",
    home_intro_point_3: "Viajes turísticos especializados y viajes por placer.",
    home_intro_point_4: "Representación de instituciones educativas internacionales.",
    home_intro_point_5: "Asesoría integral para estudiantes, familias y escuelas.",
    home_intro_end: "Nos especializamos en movilidad académica y turismo, tanto nacional como internacional, ofreciendo programas educativos, experiencias culturales y viajes diseñados para transformar vidas",
    home_intro_primary_cta: "Descubre nuestra historia",
    home_intro_secondary_cta: "Agenda una asesoría",
    home_intro_stat_years_value: "+20",
    home_intro_stat_years_label: "Años creando experiencias",
    home_intro_stat_countries_value: "+18",
    home_intro_stat_countries_label: "Destinos alrededor del mundo  ",
    home_intro_stat_students_value: "+15,000",
    home_intro_stat_students_label: "Historias transformadas",
    home_intro_stat_partners_value: "100+",
    home_intro_stat_partners_label: "Instituciones Educativas",
  home_tour_step_scroll_intro: "Desliza hacia abajo para descubrir más de nuestros servicios.",
  home_tour_step_select_category: "Selecciona la categoría que quieres explorar (Link Travel o Link Education).",
  home_tour_step_scroll_services: "Continúa deslizando hasta la sección de servicios para ver las opciones disponibles.",
  home_tour_step_select_service: "Haz clic en el servicio que más te interese para ver sus detalles.",
  home_tour_step_view_more: "Termina presionando “Ver más” para abrir la información completa del servicio.",
  home_tour_prompt_title: "¿Eres nuevo por aquí?",
  home_tour_prompt_body: "Te guiamos con un recorrido rápido por las secciones principales de la página. ¿Quieres verlo ahora?",
  home_tour_prompt_accept: "Sí, mostrar recorrido",
  home_tour_prompt_decline: "No, gracias",
  home_tour_skip: "Saltar",
  home_tour_manual_next: "Continuar",
  home_what_we_offer_banner: "¿QUÉ TE OFRECEMOS?",
  home_what_we_offer_title: "¿Qué te ofrecemos?",
  home_packages_similar: "Paquetes similares",
  home_20_years: "20 años de experiencia",
  home_slogan_1: "Cumpliendo tus sueños",
  includes_flight: "Incluye vuelo",
  view_itinerary: "Ver itinerario",
  home_slogan_2: "Confianza y compromiso",
  home_slogan_3: "Vínculos que duran",
  home_slogan_4: "Aprende, viaja, crece",
  call_cta: "Llamar",
  email_cta: "Email",
    home_offer_title: "¿Qué te ofrecemos?",
    home_carousel_1: "20 años de experiencia",
    home_carousel_2: "Cumpliendo tus sueños",
    home_carousel_3: "Confianza y compromiso",
    home_carousel_4: "Vínculos que duran",
    home_carousel_5: "Aprende, viaja, crece",
    home_connect_kicker: "// LET'S CONNECT",
    home_connect_title: "Cumpliendo metas con LET",
    home_connect_paragraph: "En Grupo LET te acompañamos de principio a fin en tu experiencia de viaje: planeación, asesoría educativa y turística, gestión de visas y alojamiento, y acompañamiento en destino. Conectamos tu proyecto con instituciones y socios acreditados y una comunidad que te impulsa.",
    home_bullet_community: "Comunidad Vibrante",
    home_bullet_advisory: "Asesoramiento Integral",
    home_bullet_experience: "Experiencia y Compromiso",
    home_ceo_role: "CEO GRUPO LET",
    home_experience_years_label: "Años de experiencia",
    home_partners_label: "NUESTROS PARTNERS",
    home_partners_title1: "Acreditaciones",
    home_partners_title2: "y alianzas estratégicas",
    home_partners_paragraph: "En LET, creemos en la colaboración como motor de crecimiento. Estas alianzas y acreditaciones avalan nuestras prácticas y potencializan oportunidades para nuestros estudiantes.",
    home_offices_title: "Nuestras Oficinas",
    home_offices_subtitle: "Presencia internacional para brindarte el mejor servicio donde te encuentres",
    home_call_button: "Llamar",
    home_email_button: "Email",
    aria_llamar_a: "Llamar a",
    aria_enviar_correo_a: "Enviar correo a",
    home_reviews_title: "Lo que dicen nuestros estudiantes",
    home_review1_text: "Excelente servicio y acompañamiento durante todo el proceso. Muy recomendable.",
    home_review2_text: "Atención personalizada excepcional. Me ayudaron a cumplir mi sueño de estudiar en Australia.",
    home_review3_text: "Profesionales comprometidos que realmente se preocupan por el éxito de sus estudiantes.",
    home_reviews_word: "reseñas",
    leave_google_review: "Dejar reseña en Google",
    loading_map: "Cargando mapa...",
    loading_globe: "Cargando vista 3D...",
    globe_unavailable_title: "3D no disponible",
    globe_unavailable: "La vista 3D requiere dependencias adicionales. Instala react-globe.gl y three.",
    // About page (Nosotros)
    about_section_title: "Sobre nosotros",
    about_who_we_are: "Quiénes Somos",
    about_card1_title: "LET | Link Education and Travel",
    about_card1_intro: "Desde el 1 de junio de 2005, LET – Link Education and Travel se ha consolidado como un grupo especializado en programas de movilidad académica y turismo, con una trayectoria que nos ha posicionado como líderes en la industria educativa y turística internacional.",
    about_card1_programs_title: "Programas que transforman vidas",
    about_card1_programs_item1: "Campamentos de verano e invierno para niños y jóvenes.",
    about_card1_programs_item2: "Viajes turísticos especializados y viajes por placer.",
    about_card1_programs_item3: "Representación oficial de universidades y escuelas canadienses en México.",
    about_card1_programs_item4: "Asesoría y acompañamiento integral para estudiantes, familias e instituciones.",
    about_card2_eyebrow: "Expansión y Filosofía",
    about_card2_title: "Presencia global",
    about_card2_p1: "En 2018, LET abrió oficinas y operaciones directas en Canadá, con el propósito de fortalecer los vínculos entre ambos países, ofrecer atención local a estudiantes e instituciones, y ampliar nuestros servicios hacia nuevos proyectos internacionales.",
    about_card2_p2: "Hoy, LET se proyecta como un grupo global en constante crecimiento, con presencia y alianzas estratégicas en América Latina, Europa y Asia, creando puentes que unen oportunidades y transforman vidas a través de la educación, la cultura y el turismo.",
    about_card2_p3: "Con el respaldo de un equipo multicultural y la participación activa en las principales cámaras y asociaciones educativas y turísticas en México y Canadá, LET continúa liderando la movilidad internacional con innovación, integridad y compromiso humano.",
    about_history_subtitle: "Hitos que han marcado nuestro camino.",
    about_mission_vision_title: "Misión y Visión",
    about_mission_label: "Misión",
    about_mission_title: "Acompañarte en cada trámite y viaje",
    about_mission_text: "Proveer soluciones confiables y ágiles en gestión de trámites y servicios de viaje, ofreciendo atención personalizada y resultados medibles que inspiren confianza en cada paso.",
    about_vision_label: "Visión",
    about_vision_title: "Ser tu aliado estratégico por 20+ años más",
    about_vision_text: "Consolidarnos como la referencia nacional en servicios integrales para viajeros y organizaciones, innovando con tecnología, excelencia operativa y una cultura centrada en las personas.",
    about_values_title: "Nuestros Valores",
    about_values_honestidad_title: "Honestidad",
    about_values_honestidad_text: "Actuamos con transparencia y sinceridad en cada relación y decisión.",
    about_values_confianza_title: "Confianza",
    about_values_confianza_text: "Procesos claros, métricas y mejora continua para resultados superiores.",
    about_values_integracion_title: "Integración",
    about_values_integracion_text: "Transparencia y cumplimiento que sostienen relaciones de largo plazo.",
    about_values_tolerancia_title: "Tolerancia",
    about_values_tolerancia_text: "Tiempo de respuesta rápido con comunicación clara en cada etapa.",
    about_values_calidad_title: "Calidad",
    about_values_calidad_text: "Trabajamos como socios estratégicos para alcanzar tus objetivos.",
    about_overlay_years: "AÑOS",
    about_overlay_phrase: "VIAJANDO CONTIGO",
  nav_divisas: "Tipo de Cambio",
  nav_pagos: "Pago en línea",
  nav_lets_study: "Link Education",
  nav_lets_study_desc: "Programas académicos",
  nav_lets_travel: "Link Travel",
  nav_participations: "Alianzas",
  nav_visas: "Visas",
  nav_lets_inmigrate: "Let's Immigrate",
  nav_lets_inmigrate_desc: "Trámites migratorios",
  lets_inmigrate_australia: "Migración a Australia",
  nav_blog: "Blog",
  lets_study_children_languages: "Idiomas",
  lets_study_children_work_and_study: "Estudia y trabaja",
  lets_study_children_highschool: "High school",
  lets_study_children_aupair: "Au Pair",
  lets_study_children_pathway: "Pathway",
  lets_study_children_higher_education: "Educación Superior",
  lets_study_children_online_english: "Inglés online",
  lets_study_children_educational_groups: "Grupos Educativos Internacionales",
  lets_study_children_visas: "Trámites migratorios",
  lets_study_children_expo: "Expo Estudia Viajando 2027",
  lets_travel_children_visa_americana: "Visa Americana",
  lets_travel_children_visa_canadiense: "Visa Canadiense",
  lets_travel_children_international_travel: "Viajes internacionales",
  lets_travel_children_domestic_travel: "Viajes nacionales",
  lets_travel_children_bachelor_parties: "Despedida de solteros",
  lets_travel_children_honeymoons: "Lunas de Miel",
  lets_travel_children_cruises: "Cruceros",
  lets_travel_children_quinceaneras: "Quinceañeras",
  lets_travel_children_destination_weddings: "Bodas",
  lets_travel_children_personalized_trips: "Viajes Personalizados",
  secondary_services_nav_aria: "Servicios Travel",
  // Service descriptions added from user's copy
  lets_study_children_languages_desc: "Cursos intensivos para aprender un idioma en destinos donde se habla de forma nativa.",
  lets_study_children_work_and_study_desc: "Programas que combinan estudios con experiencia laboral en el extranjero.",
  lets_study_children_higher_education_title: "Educación Superior",
  lets_study_children_higher_education_desc: "Licenciaturas, maestrías y doctorados en universidades de prestigio mundial.",
  lets_study_children_highschool_desc: "Programas diseñados para preparar académica y lingüísticamente a alumnos en el extranjero.",
  lets_study_children_aupair_desc: "Vive con una familia anfitriona, cuida de sus hijos y enriquece tu experiencia cultural.",
  lang_toggle_aria: "Cambiar idioma",
  menu_open_aria: "Abrir menú",
  menu_close_aria: "Cerrar menú",
  social_facebook: "Facebook",
  social_instagram: "Instagram",
  social_youtube: "YouTube",
  social_linkedin: "LinkedIn",
  brand_alt: "Grupo LET",
    // Services & hero extras
    btn_see_more: "Ver más",
    btn_explore: "Explorar",
  learn_more: "Conocer más",
  quote_here: "Ver más",
  wa_short: "WA",
    services_relevantes_title: "Relevantes",
    services_packages_similar: "Paquetes similares",
    services_why_choose_title: "¿Por qué elegirnos?",
    services_process_title: "Nuestro proceso",
    services_faq_title: "Preguntas frecuentes",
    services_no_subservices: "No hay sub-servicios disponibles por ahora.",
    // Labels for editable fields
    service_label: "Servicio",
    subservice_label: "Sub-servicio",
    mapa_destinos_title: "Mapa de destinos",
    destinations_available: "Destinos Disponibles",
    no_destinations_for_service: "No hay destinos para este servicio.",
    image_unavailable: "Imagen no disponible",
    suggested_activities: "Actividades Sugeridas",
    select_destination_prompt: "Selecciona un destino de la lista para ver los detalles.",
    view_gallery: "Ver galería",
    view_details: "Ver detalles",
    description_label: "Descripción",
    // Jobs module (Bolsa de trabajo)
    jobs_title_prefix: "OPORTUNIDADES LABORALES EN",
    jobs_title_highlight: "LET",
    jobs_subtitle: "Encuentra la posición ideal para ti. Filtra por ubicación, área y tipo de puesto.",
    jobs_search_placeholder: "Buscar por título, área o ubicación",
    jobs_quick_all: "Todos",
    jobs_quick_remote: "Remoto",
    jobs_quick_mexico: "México",
    jobs_quick_canada: "Canadá",
    jobs_quick_marketing: "Marketing",
    jobs_quick_operations: "Operaciones",
    jobs_filter_location: "Ubicación",
    jobs_filter_location_all: "Todas",
    jobs_filter_location_mexico: "México",
    jobs_filter_location_canada: "Canadá",
    jobs_filter_location_australia: "Australia",
    jobs_filter_location_remote: "Remoto",
    jobs_filter_department: "Departamento",
    jobs_filter_department_all: "Todos",
    jobs_filter_department_sales: "Ventas",
    jobs_filter_department_operations: "Operaciones",
    jobs_filter_department_marketing: "Marketing",
    jobs_filter_department_legal: "Legal",
    jobs_filter_sort_by: "Ordenar por",
    jobs_sort_relevantes: "Más relevantes",
    jobs_sort_recientes: "Más recientes",
    jobs_sort_remoto: "Remoto primero",
    jobs_sort_sueldo: "Mejor sueldo",
    jobs_loading: "Cargando…",
    jobs_results_word: "resultados",
    jobs_clear: "Limpiar",
    jobs_badge_featured: "Destacado",
    jobs_badge_urgent: "Urgente",
    jobs_view_details: "Ver detalles",
    jobs_apply_now: "Aplicar ahora",
    jobs_load_more: "Cargar más",
    jobs_empty_title: "No encontramos posiciones",
    jobs_empty_desc: "Intenta ajustar tus filtros de búsqueda o envíanos tu CV para futuras oportunidades.",
    jobs_send_cv: "ENVIAR CV",
    jobs_salary: "Sueldo",
    jobs_save: "Guardar",
    jobs_share: "Compartir",
    jobs_link_copied: "Enlace copiado",
    jobs_dialog_apply_title: "Aplicar a la vacante",
    jobs_dialog_apply_vacancy: "Vacante",
    jobs_field_name: "Nombre",
    jobs_field_email: "Email",
    jobs_field_whatsapp: "WhatsApp",
    jobs_field_cvurl: "URL de CV (Drive/Dropbox)",
    jobs_field_message_optional: "Mensaje (opcional)",
    jobs_send: "Enviar",
    jobs_application_sent: "Aplicación enviada",
    jobs_application_error: "Error al enviar la aplicación",
    // Jobs Benefits & Culture
    jobs_benefits_title_prefix: "MÁS QUE UN",
    jobs_benefits_title_highlight: "TRABAJO",
    jobs_benefits_subtitle: "Una experiencia de crecimiento personal y profesional única",
    jobs_benefit_international_title: "Experiencia Internacional",
    jobs_benefit_international_desc: "Oportunidades de trabajo en Canadá, Australia y otros destinos",
    jobs_benefit_professional_title: "Desarrollo Profesional",
    jobs_benefit_professional_desc: "Capacitación continua y certificaciones internacionales",
    jobs_benefit_multicultural_title: "Equipo Multicultural",
    jobs_benefit_multicultural_desc: "Trabaja con personas de diferentes países y culturas",
    jobs_benefit_medical_title: "Seguro Médico Premium",
    jobs_benefit_medical_desc: "Cobertura completa para ti y tu familia",
    jobs_benefit_bonus_title: "Bonos por Resultados",
    jobs_benefit_bonus_desc: "Recompensas adicionales basadas en tu desempeño",
    jobs_benefit_flex_title: "Flexibilidad Laboral",
    jobs_benefit_flex_desc: "Horarios flexibles y opciones de trabajo remoto",
    jobs_culture_title_prefix: "NUESTROS",
    jobs_culture_title_highlight: "VALORES",
    jobs_culture_subtitle: "Lo que nos hace únicos como equipo y empresa",
    jobs_value_passion_title: "Pasión por Transformar Vidas",
    jobs_value_passion_desc: "Creemos en el poder de la educación internacional para cambiar destinos y crear oportunidades. Nos motiva ayudar a cada persona a alcanzar su máximo potencial.",
    jobs_value_team_title: "Trabajo en Equipo",
    jobs_value_team_desc: "Colaboramos, nos apoyamos y crecemos juntos. El éxito de uno es el éxito de todos. Fomentamos la comunicación abierta y el respeto mutuo.",
    jobs_value_excellence_title: "Excelencia y Calidad",
    jobs_value_excellence_desc: "Nos esforzamos por la excelencia en cada detalle, desde la primera consulta hasta el seguimiento. Buscamos siempre superar las expectativas.",
    jobs_value_diversity_title: "Diversidad e Inclusión",
    jobs_value_diversity_desc: "Valoramos y respetamos las diferencias culturales y personales. Creemos que la diversidad nos enriquece y nos hace mejores.",
    jobs_value_integrity_title: "Integridad",
    jobs_value_integrity_desc: "Actuamos con honestidad y ética profesional en cada proceso. La confianza de nuestros estudiantes y colaboradores es nuestra prioridad.",
    jobs_value_innovation_title: "Innovación",
    jobs_value_innovation_desc: "Impulsamos nuevas ideas para mejorar la experiencia de nuestros estudiantes y colaboradores. Nos adaptamos al cambio y buscamos soluciones creativas.",
    jobs_cta_title_prefix: "¿LISTO PARA",
    jobs_cta_title_highlight: "COMENZAR?",
    jobs_cta_paragraph: "Únete a un equipo que está transformando vidas y construyendo futuros. Tu próxima aventura profesional te espera.",
    jobs_step_simple_process: "Proceso de aplicación simple",
    jobs_step_48h_response: "Respuesta en 48 horas",
    jobs_step_team_interviews: "Entrevistas con el equipo",
    jobs_step_onboarding: "Onboarding personalizado",
    jobs_btn_send_my_cv: "ENVIAR MI CV",
    jobs_btn_contact_hr: "CONTACTAR RH",
    
    // Australia page (ES)
    auss_meta_title: "LET - Viva Australia | Impulsa Tu Futuro en Australia",
    auss_whatsapp_text: "Hola! Quiero conocer más de sus servicios",
    auss_whatsapp_tooltip: "¡Chatea con nosotros!",
    auss_hero_title: "Impulsa Tu Futuro en Australia",
    auss_hero_tagline: "Una Alianza por y para el Talento Mexicano",
    auss_hero_subtitle: "Descubre cómo LET y VIVA Australia colaboramos para abrirte la puerta a experiencias laborales inolvidables en un pais con un sinfin de oportunidades.",
    auss_cta_more: "¡Quiero Saber Más!",
    
    auss_alliance_badge: "Alianza Estratégica Formalizada",
    auss_alliance_title: "Una Alianza por y para el Talento Mexicano",
    auss_alliance_subtitle: "La colaboración surge del interés conjunto por ofrecer soluciones integrales a los jóvenes mexicanos que buscan experiencias laborales en el extranjero, con respaldo confiable, asesoría personalizada y acompañamiento continuo.",
    auss_alliance_h3: "¿Por qué esta alianza?",
    auss_alliance_h4: "Un puente entre México y Australia",
    auss_alliance_p1_bold: "Complementariedad de servicios:",
    auss_alliance_p1_rest: " LET aporta su sólida trayectoria en asesoría y preparación para destinos como Australia, mientras que Viva Australia refuerza el componente legal y migratorio con su experiencia especializada en visas, regulaciones migratorias y representación ante autoridades australianas.",
    auss_alliance_p2_bold: "Asesoría integral en un solo lugar:",
    auss_alliance_p2_rest: " Gracias a esta alianza, los estudiantes recién egresados, próximos a egresar y profesionistas podrán acceder a una solución 100% integrada.",
    auss_alliance_p3_bold: "Confianza y respaldo profesional:",
    auss_alliance_p3_rest: " Ambas organizaciones cuentan con acreditaciones clave como MARA, MIA, QEAC, CRICOS y alianzas con universidades e instituciones reconocidas, lo que garantiza que cada proceso se maneje con total transparencia y conforme a la legislación vigente.",
    auss_alliance_p4_bold: "Cobertura regional y cercanía con el cliente:",
    auss_alliance_p4_rest: " Con presencia en Latinoamérica y Australia, esta alianza permite ofrecer un acompañamiento cercano, antes y durante el viaje, con equipos locales que entienden tanto el contexto de origen como el del país destino.",
    auss_youtube_alt: "Alianza Viva Australia & LET",
    
    auss_benefits_badge: "Objetivos de la Alianza",
    auss_benefits_title: "Beneficios Exclusivos de Nuestra Alianza",
    auss_benefits_subtitle: "Hemos diseñado cada ventaja para que tu proyecto en Australia sea más que un sueño: una realidad estratégica y exitosa que impulse el talento mexicano hacia un futuro global.",
    auss_benefit1_title: "Tu Éxito, Nuestra Guía",
    auss_benefit1_desc: "Recibe una asesoría experta y personalizada que traza el mapa exacto para tu desarrollo profesional en Australia.",
    auss_benefit2_title: "Viaja con Total Tranquilidad",
    auss_benefit2_desc: "Disfruta de un acompañamiento 360°, desde visados hasta tu adaptación, eliminando cualquier preocupación.",
    auss_benefit3_title: "Impulsa Tu Carrera Global",
    auss_benefit3_desc: "Accede a un sinfín de oportunidades profesionales en una de las economías más dinámicas del mundo.",
    auss_benefit4_title: "El Poder de Dos Expertos",
    auss_benefit4_desc: "La experiencia combinada LET y VIVA Australia, dos agencias de confianza reconocidas internacionalmente.",
    
    auss_partners_badge: "Conócenos",
    auss_partners_title: "Quiénes Somos",
    auss_partners_subtitle: "Dos organizaciones lideres unidas por un objetivo común; hacer realidad los sueños profesionales de los jovenes mexicanos en Australia.",
    auss_partner_let_slogan: "\"Somos tu forma de llegar al mundo\"",
    auss_partner_let_desc: "Grupo LET, es una empresa mexicano-canadiense especializada en el turismo educativo y turismo de placer. Fundada en la ciudad de Toluca, en el año 2005. Hemos sido parte de más de 15,347 experiencias y sueños cumplidos, manteniendo la calidad y profesionalismo en nuestros servidos.\n\nCompromiso: Fortalecer nuestro propósito como institución, abriendo caminos globales a través de la formación, el acompañamiento y la movilidad internacional.",
    auss_partner_viva_slogan: "\"Hacer realidad tu proyecto de vida en Australia\"",
    auss_partner_viva_desc: "VIVA Australia es una agencia australiana con presencia en América Latina y Australia desde 1999, especializada en migración y educación. Ofrecemos asesoría profesional de alto nivel para quienes buscan una experiencia académica o laboral en Australia.\n\nCompromiso: Guiar a estudiantes latinos hacia programas de estudio de calidad y facilitar a profesionales el ejercicio de su carrera en el país.",
    
    auss_slider1_title: "Tu futuro en Australia empieza hoy",
    auss_slider1_desc: "No dejes pasar la oportunidad de descubrir cómo nuestra alianza entre LET y Viva Australia puede abrirte las puertas a experiencias transformadoras. Nuestro equipo está listo para brindarte la asesoría que necesitas para dar el primer paso hacia tu futuro australiano.",
    auss_slider1_cta: "Agendar Asesoría Gratuita",
    auss_slider2_title: "Australia: Donde tus sueños profesionales se hacen realidad.",
    auss_slider2_desc: "Con el respaldo de LET y Viva Australia, tu ambición de crecer y brillar en el ámbito laboral internacional encuentra su camino. Este país te ofrece el escenario perfecto para construir la carrera que siempre has imaginado, rodeado de innovación y oportunidades sin igual.",
    auss_slider2_cta: "Explorar Oportunidades",
    
    auss_target_badge: "Para Ti",
    auss_target_title: "¿Es Esta Tu Oportunidad?",
    auss_target_h2: "¿A quién va dirigida esta oportunidad?",
    auss_target_h5: "Este programa es para ti si:",
    auss_target_item1: "Buscas realizar practicas profesionales en Australia",
    auss_target_item2: "Necesitas perfeccionar tu inglés",
    auss_target_item3: "Buscas desarrollar habilidades globales en tu área profesional",
    auss_target_item4: "Quieres transformar tu vida",
    auss_target_item5: "Quieres experiencia laboral internacional",
    auss_target_item6: "Deseas explorar nuevas culturas",
    
    auss_contact_title: "¡Comienza Tu Aventura Australiana!",
    auss_contact_subtitle: "Completa el formulario y recibe asesoría personalizada gratuita para planificar tu futuro en Australia.",
  },
  EN: {
  calculator_title: "Currency Exchange Calculator",
  amount_label: "Amount",
  from_label: "From",
  tipo_cambio_conoce_mas: "Learn more ",
  lets_travel_children_corporate_travel: "Corporate Travel",
  // Terms / Privacy Notice
  Pagos_title: "Make your payment online.",
  // Pagos / Payment page
  "pagos.client_title": "Client information",
  "pagos.client_name_label": "Full name",
  "pagos.client_email_label": "Email",
  "pagos.client_phone_label": "Phone",
  "pagos.client_country_label": "Country",
  "pagos.service_category_label": "Service category",
  "pagos.service_select_label": "Service to pay",
  "pagos.currency_label": "Currency",
  "pagos.amount_label": "Amount (no cents)",
  "pagos.card_data_title": "Card details",
  "pagos.card_holder_label": "Cardholder (as on card)",
  "pagos.card_number_label": "Card number",
  "pagos.card_exp_month_label": "Expiration month",
  "pagos.card_exp_year_label": "Expiration year",
  "pagos.card_cvv_label": "CVV",
  "pagos.note_currency_hint": "* We suggest the currency based on the country, but you can change it before paying.",
  "pagos.canada_surcharge_note": "* A 4% processing fee is added for Canada (equivalent to {surcharge} {currency}).",
  "pagos.pay_button": "Pay",
  "pagos.processing": "Processing...",
  "pagos.privacy_notice_link": "View privacy notice",
  "pagos.payment_error_title": "Payment could not be processed",
  "pagos.close": "Close",
  // Receipt / Pago confirmado
  "pagos.badge_receipt": "Digital receipt",
  "pagos.confirmed_title": "Your payment is confirmed",
  "pagos.no_recent_payment_title": "No recent payment found",
  "pagos.confirmed_description": "Download your ticket with full charge details and share the confirmation with your Link advisor.",
  "pagos.no_recent_payment_description": "The current session doesn't have a confirmed payment. Return to the form to register a new one.",
  "pagos.download_ticket": "Download PDF ticket",
  "pagos.another_payment": "Make another payment",
  "pagos.no_payment_heading": "No recent payment",
  "pagos.no_payment_description": "We couldn't find a confirmed payment to show. Go back to the form to try again.",
  "pagos.go_to_payments": "Go to payments",

  // PDF specific
  "pagos.pdf_title": "Payment receipt",
  "pagos.pdf_subtitle": "Grupo LET | Operated by OpenPay",
  "pagos.pdf_amount_paid": "AMOUNT PAID",
  "pagos.pdf_status": "STATUS",
  "pagos.pdf_transaction": "TRANSACTION",
  "pagos.pdf_details": "PAYMENT DETAILS",
  "pagos.pdf_observations": "Notes",
  "pagos.pdf_obs_line1": "This receipt confirms that the charge was processed through OpenPay.",
  "pagos.pdf_obs_line2": "Keep this document for any clarification.",
  "pagos.pdf_contact": "Contact: +52 722 235 2370 · WhatsApp +52 712 231 5859",
  "pagos.pdf_address_line1": "Address: Calle Andres Quintana Roo #529, Col. Francisco Murguia, El Ranchito,",
  "pagos.pdf_address_line2": "C.P. 50130, Toluca, Mexico",
  "pagos.pdf_logo_error_title": "ERROR: Could not load SVG logo",
  "pagos.pdf_logo_error_details": "Check the browser console for details.",
  "pagos.verifying_openpay": "Verifying with OpenPay, one moment...",
  "pagos.openpay_status_report": "OpenPay reported status: {status}. If the charge completed, contact support.",
  "pagos.openpay_verify_error": "Could not verify the payment status.",
  "pagos.download_error": "Could not generate the ticket. Try again.",
  terms_toc_contents: "Contents",
  terms_print: "Print",
  terms_header_privacy_notice: "Privacy Notice",
  terms_intro_sentence: "The website grupo-let.com, operated by Link Education & Travel (LET), is responsible for the collection, use and protection of your personal data.",
  terms_section1_title: "Data Controller",
  terms_section1_body: "LET is the data controller for personal information related to educational and travel services offered on grupo-let.com.",
  terms_section2_title: "Collection of Personal Data",
  terms_section2_intro: "We may request, as applicable, the following documentation to process your services:",
  terms_section2_item1: "Forms from the educational advisor or the website.",
  terms_section2_item2: "Proof of payment and related service documents.",
  terms_section2_item3: "Information to verify the applicant's identity.",
  terms_section2_item4: "Property documents (if required by the procedure).",
  terms_section2_item5: "Invoices to validate payments and related transactions.",
  terms_section3_title: "Use of Personal Data",
  terms_section3_item1: "Verify the applicant's identity.",
  terms_section3_item2: "Process applications and manage educational and travel procedures.",
  terms_section3_item3: "Manage user relationships and keep them informed.",
  terms_section3_item4: "Handle charges and payments related to our services.",
  terms_section3_note_summary: "Note on additional purposes",
  terms_section3_note_body: "If we intend to use your data for purposes other than those stated, we will inform you and, where applicable, request your consent.",
  terms_section4_title: "Third-party Links",
  terms_section4_body: "This site may contain links to third-party websites. LET is not responsible for their privacy practices or content. We recommend reviewing their policies.",
  terms_section5_title: "Policy Updates",
  terms_section5_body: "We periodically review our practices and update this policy. Please check it regularly for changes.",
  terms_section6_title: "Google Analytics & Remarketing",
  terms_section6_body_part1: "This site uses Google Analytics and display advertising (Remarketing). More information at",
  terms_section6_body_part2: "You can manage your Google ads preferences.",
  terms_section7_title: "Questions & Comments",
  terms_section7_intro: "If you have questions about this policy or our practices, contact us:",
  terms_section7_phone: "Phone",
  terms_section7_address: "Address",
  terms_section8_title: "Confidentiality & Security",
  terms_section8_body: "LET maintains the confidentiality of personal data and applies reasonable security measures to protect it from unauthorized access, misuse or disclosure.",
  terms_section9_title: "Payments",
  terms_section9_body: "Payments will be processed through the Openpay gateway.",
  terms_last_update: "Last update:",
  terms_last_update_date: "September 15, 2025",
  terms_skip_to_content: "Skip to content",
  terms_toc_label: "Table of contents",
  terms_anchor_aria: "Permanent link",
  terms_print_aria: "Print",
  terms_back_to_top: "Back to top",
  // Admin menu and dashboard
  admin_dashboard: "Admin Dashboard",
  admin_choose_section: "Choose a section:",
  admin_menu_divisas: "Rates",
  admin_menu_blog_posts: "Blog - Posts",
  admin_menu_blog_articles: "Blog - Articles",
  admin_menu_blog_trending: "Blog - Trending",
  admin_menu_testimonials: "Testimonials",
  admin_menu_popups: "Popups",
  admin_label: "Admin",
  admin_items: "items",
  admin_configurations: "configurations",
  admin_common_active: "Active",
  admin_common_inactive: "Inactive",
  admin_common_saved: "Saved",
  admin_common_save_error: "Save error",
  admin_common_delete_error: "Delete error",
  admin_common_update: "Update",
  admin_common_create: "Create",
  admin_common_add: "Add",
  // Blog posts
  admin_blog_posts: "Blog Posts",
  admin_blog_no_posts: "No posts.",
  admin_blog_select_new_or_edit_post: "Click \"New\" or pick a post to edit.",
  admin_post_kind_blog: "Blog",
  admin_post_kind_noticias: "News",
  admin_post_kind_webinars: "Webinars",
  // Promotions
  admin_promotions: "Promotions",
  admin_promos_manage_desc: "Manage promotions with token-based security",
  admin_promos_id_title_required: "ID and Title are required",
  admin_token_persistence: "Stored in \"admin_token\" cookie and localStorage.",
  admin_save_token: "Save token",
  admin_promos_description_ph: "Describe the promotion. You can use HTML formatting, bold, lists, etc.",
  promos_filter_title: "Filter promotions",
  promos_filter_description: "Select a category to show only related promotions.",
  admin_field_subtitle: "Subtitle",
  admin_field_price_original: "Original Price",
  admin_field_price_final: "Final Price",
  admin_field_currency: "Currency",
  admin_field_discount: "% Discount",
  admin_field_validity: "Validity",
  admin_field_badge: "Badge",
  admin_field_package_link: "Package Link",
  admin_field_whatsapp: "WhatsApp",
  admin_field_travel_dates: "Travel dates",
  admin_promos_none: "No promotions",
  admin_promos_confirm_delete: "Delete this promotion?",
  // Popup
  admin_popup_confirm_delete: "Delete this popup?",
  admin_field_cta_label: "CTA Label",
  admin_field_cta_link: "CTA Link",
  admin_field_allow_close: "Allow closing",
  admin_field_paths: "Paths (comma-separated)",
  admin_field_paths_ph: "/, /blog, /divisas, /blog*",
  admin_field_start_iso: "Start (ISO)",
  admin_field_end_iso: "End (ISO)",
  admin_triggers: "Triggers",
  admin_trigger_onload: "On load",
  admin_trigger_exit_intent: "Exit intent",
  admin_field_delay_ms: "Delay (ms)",
  admin_field_scroll_percent: "Scroll (%)",
  admin_frequency: "Frequency",
  admin_frequency_once: "Once per session",
  admin_hide_preview: "Hide preview",
  admin_show_preview: "Show preview",
  admin_more_info: "More info",
  // Trending
  admin_blog_trending: "Blog Trending",
  admin_trending_type_articulo: "Article",
  admin_trending_type_guia: "Guide",
  admin_trending_type_promocion: "Promotion",
  admin_trending_type_paquete: "Package",
  // Products admin
  admin_products: "Products",
  admin_products_desc: "Configure the content for each product/service",
  admin_products_required: "Slug, title and description are required",
  admin_products_selected_service: "Selected service",
  admin_products_subtitle_ph: "Product/service subtitle. You can use HTML.",
  admin_products_hero_images: "Hero Images",
  admin_products_no_images_hint: "No images: defaults per service will be used.",
  admin_products_related_packages: "Related packages (promotion-like)",
  admin_products_no_related: "No related packages",
  admin_field_days: "Days",
  admin_field_days_ph: "Number of days",
  admin_field_nights: "Nights",
  admin_field_nights_ph: "Number of nights",
  admin_field_countries: "Countries",
  admin_field_countries_ph: "Number of countries",
  admin_field_cities: "Cities",
  admin_field_cities_ph: "Number of cities",
  admin_field_include_flight: "Includes flight",
  admin_field_airline_logo_url: "Airline logo URL",
  admin_field_itinerary_pdf_url: "Itinerary PDF URL",
  admin_field_taxes_label: "Taxes label",
  
  admin_products_form_url: "Form URL (iframe)",
  admin_products_form_url_ph: "https://form.jotform.com/your-form",
  admin_products_form_url_hint: "If provided and it's a service, we'll replace the final CTA buttons with this iframe and the hero button will open this form.",
  admin_products_reasons: "Reasons (Why choose us?)",
  admin_products_reason_ph: "Describe this reason. You can use HTML.",
  admin_products_no_items: "No items",
  admin_products_steps: "Steps (process)",
  admin_products_step_ph: "Describe this step in the process. You can use HTML.",
  admin_products_hero_variant: "Hero Variant",
  admin_products_faq_q: "Question",
  admin_products_faq_a: "Answer",
  admin_products_faq_a_ph: "Detailed answer. You can use HTML, lists, links, etc.",
  admin_products_faq_cat_general: "General",
  admin_products_faq_cat_about: "About Us",
  admin_products_faq_cat_travel: "Travel",
  admin_products_faq_cat_insurance: "Insurance",
  admin_products_faq_cat_stay: "Stay",
  admin_products_faq_cat_docs: "Documentation & paperwork",
  admin_products_updated: "Updated",
  admin_products_none: "No products",
  admin_products_confirm_delete: "Delete this product?",
  // Rates
  admin_rates_title: "Exchange Rates",
  admin_rates_subtitle: "Upload rates and view history",
  admin_token_sent_header: "Sent as x-admin-token header for actions.",
  admin_rates_base: "Base",
  admin_rates_pair: "Pair (history)",
  admin_rates_history: "History",
  admin_rates_latest_records: "Latest records",
  admin_rates_save_btn: "Save rates",
  admin_no_data: "No data",
  // Jobs admin
  admin_jobs_title: "Admin Jobs",
  admin_jobs_confirm_delete: "Delete this job?",
  admin_job_open: "Open position",
  admin_job_closed: "CLOSED",
  admin_field_location: "Location",
  admin_field_department: "Department",
  admin_field_experience: "Experience",
  admin_field_salary: "Salary",
  admin_field_image_url: "Image URL",
  admin_field_link: "Link (href)",
  admin_field_link_ph: "/blog/my-article or https://...",
  admin_field_views: "Views",
  admin_field_description: "Description",
  admin_field_requirements_ph: "Requirements (one per line)",
  admin_field_benefits_ph: "Benefits (one per line)",
  // Login
  admin_login_invalid: "Invalid credentials",
  admin_login_too_many: "Too many attempts. Try again later.",
  admin_login_error: "Authentication error",
  admin_login_attempts_left: "Attempts left",
  admin_login_blocked_retry_in: "Blocked. Retry in",
  admin_login_enter: "Enter",
  // Admin common
  admin_token_label: "Admin token",
  admin_token_placeholder: "Paste your RATES_ADMIN_TOKEN",
  admin_common_new: "New",
  admin_common_edit: "Edit",
  admin_common_delete: "Delete",
  admin_common_cancel: "Cancel",
  admin_common_save: "Save",
  admin_common_remove: "Remove",
  admin_common_saving: "Saving…",
  admin_common_reload: "Reload",
  admin_common_refresh: "Refresh",
  admin_common_view: "View",
  admin_common_no_results: "No results",
  admin_common_no_image: "No image",
  admin_common_no_title: "Untitled",
  admin_common_no_date: "No date",
  admin_common_no_message: "(no message)",
  admin_common_not_provided: "(not provided)",
  admin_common_export_csv: "Export CSV",
  admin_common_unauthorized_or_error: "Unauthorized or error. Check the token.",
  // Admin table labels
  admin_table_date: "Date",
  admin_table_vacancy: "Vacancy",
  admin_table_name: "Name",
  admin_table_email: "Email",
  admin_table_whatsapp: "WhatsApp",
  admin_table_cv: "CV",
  admin_table_message: "Message",
  admin_table_status: "Status",
  admin_table_actions: "Actions",
  admin_open: "Open",
  admin_open_cv: "Open CV",
  // Applications
  admin_applications_title: "Applications",
  admin_applications_search_ph: "Search by name, email or vacancy",
  admin_application_to: "Application to",
  admin_app_status_nuevo: "new",
  admin_app_status_revisando: "reviewing",
  admin_app_status_entrevista: "interview",
  admin_app_status_oferta: "offer",
  admin_app_status_rechazado: "rejected",
  admin_app_status_contratado: "hired",
  // Testimonials
  admin_testimonials_label: "testimonials",
  admin_testimonial: "Testimonial",
  admin_testimonials_confirm_delete: "Delete this testimonial?",
  // Blog
  admin_blog_articles: "Articles",
  admin_blog_write_here: "Write your article here…",
  admin_blog_missing_title_slug: "Title or slug missing",
  admin_editor: "Editor",
  admin_editing: "Editing",
  admin_blog_select_new_or_edit: "Click \"New\" or select an article to edit.",
  admin_blog_view_article: "View article",
  admin_common_no_articles: "No articles.",
  // Fields
  admin_field_title: "Title",
  admin_field_slug: "Slug",
  admin_field_slug_ph: "my-article",
  admin_field_excerpt: "Excerpt",
  admin_field_category: "Category",
  admin_field_tags: "Tags (comma-separated)",
  admin_field_author: "Author",
  admin_field_date: "Date",
  admin_field_read_time: "Read time",
  admin_field_service: "Service",
  admin_field_cover_image: "Cover image (URL)",
  admin_field_content: "Content",
  admin_field_css: "CSS (optional)",
  admin_field_css_ph: "/* Custom styles for this article */",
  admin_field_js: "JavaScript (optional)",
  admin_field_js_ph: "// JS code for interactivity in this article",
  admin_field_video_url: "Video URL (YouTube or similar)",
  admin_field_thumbnail: "Thumbnail (optional URL)",
  admin_field_subtitle_optional: "Subtitle (optional)",
  admin_field_currency_hint: "Currency (MXN, USD)",
  admin_field_price_optional: "Price (optional)",
  admin_field_price_prefix: "Label before price",
  admin_field_price_suffix: "Label after price",
  admin_field_whatsapp_link: "WhatsApp Link",
  // Preview
  admin_preview: "Preview",
  admin_nothing_to_preview: "Nothing to preview.",
  // Rich Text Editor
  admin_rte_placeholder: "Write your content here...",
  admin_rte_edit: "Edit",
  admin_rte_preview: "Preview",
  admin_rte_helper_html: "You can use HTML: <strong>bold</strong>, <em>italic</em>, <h1>headings</h1>, <ul><li>lists</li></ul>, etc.",
  admin_rte_heading_1: "Heading 1",
  admin_rte_heading_2: "Heading 2",
  admin_rte_heading_3: "Heading 3",
  admin_rte_bold_title: "Bold",
  admin_rte_italic_title: "Italic",
  admin_rte_underline_title: "Underline",
  admin_rte_bulleted_title: "Bulleted list",
  admin_rte_numbered_title: "Numbered list",
  admin_rte_link_title: "Link",
  admin_rte_linebreak_title: "Line break",
    nav_nosotros: "ABOUT",
    nav_destinos: "DESTINATIONS",
    nav_servicios: "SERVICES",
    nav_recursos: "RESOURCES",
    nav_eventos: "EVENTS",
    nav_consulta: "FREE CONSULTATION",
    nav_promociones: "PROMOTIONS",
    nav_lets_inmigrate: "Let's Immigrate",
    nav_lets_inmigrate_desc: "Immigration procedures",
    lets_inmigrate_children_canada: "Migration Canada",
    lets_inmigrate_australia: "Migration Australia",
    lets_travel_children_eta_canadiense: "eTA Canada",
    lets_travel_children_eta_uk: "eTA UK",
    lets_travel_children_other: "Other visas",
    submenu_historia: "Learn our story",
    submenu_destinos_desc: "Countries and cities available",
    submenu_servicios_desc: "Services we offer",
    nav_lets_travel_desc : "Travel experiences",
    submenu_recursos_desc: "Guides and tools",
    submenu_eventos_desc: "Webinars and fairs",
    popular: "Popular",
    nuevo: "New",
  llamar_ahora: "CALL NOW",
  // Route-specific CTA labels (used by CTASection to change button text per pathname)
  cta_formulario: "REGISTER",
  cta_formulario_home: "REGISTER",
  cta_enroll_now: "Enroll now",
  price_from_label: "From",
  form_register_title: "Registration form",
  cta_formulario_study: "INTERESTED IN STUDYING",
  cta_formulario_divisas: "CHECK EXCHANGE RATE",
  cta_titulo_1: "READY TO START YOUR",
    cta_titulo_resaltado: "ADVENTURE?",
    cta_parrafo: "Join thousands of people who have already transformed their lives with our programs",
    cta_contactar: "CONTACT ADVISOR",
    footer_estudios: "Study abroad",
    footer_tramites: "Immigration procedures and visas",
    footer_viajes: "Tourist trips",
    footer_grupos: "International Educational Groups",
    footer_nosotros: "About us",
    footer_comunidad: "Community",
    footer_tipo_cambio: "Exchange rate",
    footer_sobre_nosotros: "About us",
    footer_sucursales: "Our branches",
    footer_bolsa: "Jobs",
    footer_blogs: "Blogs",
    footer_podcast: "Podcast",
    footer_acceso_padres: "Parents access",
    footer_promociones: "Promotions",
    footer_viajes_internacionales: "International travel",
    footer_viajes_nacionales: "Domestic travel",
    footer_despedida: "Bachelor parties",
    footer_lunas_miel: "Honeymoons",
    footer_cruceros: "Cruises",
    footer_quinceaneras: "Quinceañeras",
    footer_idiomas: "Languages",
    footer_estudia_trabaja: "Study & work",
    footer_educacion_superior: "Higher education",
    footer_highschool: "High school",
    footer_aupair: "Au Pair",
    footer_pathway: "Pathway",
    footer_ingles_online: "Online English",
    footer_americana: "American",
    footer_canada: "Canada",
    footer_toronto: "Toronto",
    footer_quebec: "Quebec",
    footer_londres: "London",
    footer_dublin: "Dublin",
    footer_vancouver: "Vancouver",
    footer_avisopriv: "Privacy notice",
    footer_terminos: "Terms and conditions",
    footer_arriba: "↑ Top",
    footer_consultar: "Check >>",
    footer_copy: "All rights reserved.",
    hero_title_1: "WE ARE YOUR WAY TO",
    hero_title_2: "REACH THE WORLD",
    hero_desc: "We are a Mexican–Canadian company specializing in educational and leisure tourism. Founded in Toluca in 2005. We’ve been part of more than 15,347 experiences and dreams fulfilled, maintaining quality and professionalism in our services.",
    hero_hashtag: "#LiveLearnGrow",
  home_intro_video_alt: "LET experiences in action",
  podcast_hero_title: "A PODCAST MADE FOR YOU",
  podcast_recent_episodes: "Recent episodes",
  podcast_brand_label: "LET'S TALK",
  podcast_stats_error: "Channel statistics could not be loaded",
  podcast_impact_title: "Impact of the Podcast",
  podcast_impact_subtitle: "Real-time metrics of the YouTube channel",
  podcast_metric_videos: "Channel videos",
  podcast_metric_subscribers: "Subscribers",
  podcast_metric_views: "Channel views",
  podcast_why_title: "Why listen to us?",
  podcast_why_subtitle: "Curated content to help you make clear decisions and move forward without noise",
  podcast_why_item1_title: "Professional Production",
  podcast_why_item1_desc: "Clear audio, effective structure and editing focused on practical value, not filler.",
  podcast_why_item1_badge: "Sound Quality",
  podcast_why_item2_title: "Verified Content",
  podcast_why_item2_desc: "Up-to-date information on scholarships, visas, processes and opportunities with reliable sources.",
  podcast_why_item2_badge: "Rigorous",
  podcast_why_item3_title: "Real Stories",
  podcast_why_item3_desc: "Authentic testimonials from students who transformed their international lives.",
  podcast_why_item3_badge: "Inspiration",
  podcast_why_item4_title: "Actionable Focus",
  podcast_why_item4_desc: "Each episode ends with concrete steps so you can move forward immediately.",
  podcast_why_item4_badge: "Applicable",
  podcast_faq_q1: "How often do you release new episodes?",
  podcast_faq_a1: "We publish a main episode every Tuesday and short bonus episodes some Thursdays with updates or quick tips.",
  podcast_faq_a2: "You can suggest a topic or invite someone by sending us a WhatsApp message or using the contact form; click 'Suggest topic' to open the chat.",
  podcast_faq_a3: "Yes — if you'd like to appear as a guest, send a short bio and topic idea via the 'Suggest topic' button and our team will reach out.",
  podcast_faq_a4: "Resources are linked in each episode's YouTube description and on the episode page. Ask the chat if you need direct links.",
  podcast_faq_a5: "No — the podcast is free to listen to.",
  podcast_faq_q2: "How do I suggest a topic or invite someone?",
  podcast_faq_q3: "Can I appear as a guest?",
  podcast_faq_q4: "Where can I access mentioned resources?",
  podcast_faq_q5: "Is the podcast paid?",
  podcast_any_other_question: "Any other question?",
  podcast_write_to_us_cta: "Write to us and we'll turn it into an episode",
  podcast_suggest_topic: "SUGGEST TOPIC",
  // Exchange rates (currency) page
  tipo_cambio_title_prefix: "EXCHANGE RATE",
  tipo_cambio_title_highlight: "TODAY",
  tabs_recomendaciones: "Recommendations",
  tabs_facturacion: "Invoicing",
  factura_electronica_title: "Electronic invoice",
  factura_electronica_paragraph: "Request your electronic invoice quickly and securely.",
  factura_solicitar: "Request invoice",
  need_help_title: "Need help?",
  copy_result: "Copy result",
  // JoinChat / WhatsApp messages
  joinchat_label_open: "Open chat",
  joinchat_pill_text: "Have questions?",
  joinchat_root_message: "Hi Grupo LET 👋 I'm on your site and would like more information. I'm reaching out from your website.",
  joinchat_podcast_message: "Hi 👋 I listened to your podcast and have some questions. I'm reaching out from your website.",
  joinchat_promotions_message: "Hi 👋 I saw your promotions and I'm interested in more details. I'm reaching out from your website.",
  joinchat_category_study_message: "Hi 👋 I'm interested in studying abroad and would like advice. I'm reaching out from your website.",
  joinchat_category_travel_message: "Hi 👋 I want information about trips and packages. I'm reaching out from your website.",
  joinchat_tipo_cambio_message: "Hi 👋 I have questions about the exchange rate, can you help me? I'm reaching out from your website.",
  // Service-specific JoinChat messages
  joinchat_service_idiomas_message: "Hi 👋 I'm interested in language courses — can you share more information? I'm reaching out from your website.",
  "joinchat_service_work-and-study_message": "Hi 👋 I'm interested in the Study & Work program — how does it work? I'm reaching out from your website.",
  "joinchat_service_educacion-superior_message": "Hi 👋 I'd like information about Higher Education and university options. I'm reaching out from your website.",
  joinchat_service_highschool_message: "Hi 👋 I want to learn more about High School programs. I'm reaching out from your website.",
  joinchat_service_aupair_message: "Hi 👋 I'm interested in the Au Pair program — what are the requirements? I'm reaching out from your website.",
  joinchat_service_nacionales_message: "Hi 👋 I'd like information about domestic travel packages. I'm reaching out from your website.",
  joinchat_service_internacionales_message: "Hi 👋 I'm interested in international travel — which destinations do you offer? I'm reaching out from your website.",
  joinchat_service_quince_message: "Hi 👋 I want information about 15th birthday trips. I'm reaching out from your website.",
  joinchat_service_despedidas_message: "Hi 👋 I'm looking to organize a bachelor party trip — what options do you have? I'm reaching out from your website.",
  joinchat_service_grupos_message: "Hi 👋 I need information about group travel and coordination. I'm reaching out from your website.",
  joinchat_service_bodas_message: "Hi 👋 I'd like to know about wedding destinations and available packages. I'm reaching out from your website.",
  joinchat_service_personalizado_message: "Hi 👋 I'm interested in a custom trip — how do you organize those? I'm reaching out from your website.",
  // WhatsApp UI labels
  whatsapp_button: "WhatsApp",
  whatsapp_title: "WhatsApp",
  // small labels
  joinchat_close: "Close",
    services_title: "OUR SERVICES",
    services_subtitle: "Discover the opportunities we have for you. Each program is designed to deliver a unique and transformative experience.",
  participaciones_hero_title: "Make your dreams come true",
  participaciones_hero_subtitle: "Achieving goals with LET",
  participaciones_experience_caption: "Over 15 years of experience supporting international dreams",
  participaciones_stat_students: "Successful students",
  participaciones_stat_countries: "Destination countries",
  participaciones_stat_success: "Success rate",
  participaciones_stat_institutions: "Institutions",
  participaciones_process_title: "Your process in 4 steps",
  participaciones_process_subtitle: "A simple, transparent process to make your international dream come true",
  participaciones_step_1_title: "Free Consultation",
  participaciones_step_1_desc: "We evaluate your profile and define the best program for you",
  participaciones_step_2_title: "Application",
  participaciones_step_2_desc: "We help you with all the documentation and visa process",
  participaciones_step_3_title: "Preparation",
  participaciones_step_3_desc: "We organize accommodation, flights and everything needed",
  participaciones_step_4_title: "On-site Support",
  participaciones_step_4_desc: "We accompany you throughout your stay",
  participaciones_faq_title: "Frequently Asked Questions",
  participaciones_faq_q1: "What services does LET offer?",
  participaciones_faq_a1: "We offer specialized services in international education, including educational advising, visa processing, accommodation and full support during your experience abroad.",
  participaciones_faq_q2: "How long does the student visa process take?",
  participaciones_faq_a2: "Times vary by country and visa type; the process can take from a few weeks to several months. We recommend starting early and checking the specific requirements of your destination country.",
  participaciones_faq_q3: "Can I work while studying?",
  participaciones_faq_a3: "It depends on the visa and country. Many student visas allow part-time work; we advise you on rules and limits so you can combine study and work legally.",
  participaciones_faq_q4: "Do you provide follow-up once I'm at the destination?",
  participaciones_faq_a4: "Yes. We offer ongoing support and follow-up during your stay, including help with accommodation, integration, and any issues that may arise.",
    services_study_about: "A comprehensive opportunity to develop autonomy and independence while strengthening academic, emotional, and social skills.",
    services_travel_about: "Explore fascinating destinations while you learn and grow. Educational trips designed to expand your view of the world.",
    services_work_about: "Work opportunities in Canada that combine employment and learning, developing professional skills in an international environment.",
    stats_experiencias: "Experiences completed",
    stats_anios: "Years of experience",
    stats_satisfaccion: "Satisfaction guaranteed",
    testimonials_title: "SUCCESS STORIES",
    testimonials_subtitle: "Discover real stories of students who transformed their lives with our programs",
    testimonial_1_role: "Link Education student",
    testimonial_1_content: "Grupo LET completely changed my life. The study program in Canada opened doors I never imagined. The support was incredible from day one.",
    testimonial_2_role: "Link Work participant",
    testimonial_2_content: "Working in Canada while learning English was invaluable. I now have a solid professional future and an advanced level of English.",
    testimonial_3_role: "Link Travel traveler",
    testimonial_3_content: "LET’s educational trips are more than tourism. I learned so much about Canadian culture while improving my English. Unforgettable experience!",
    home_relevantes_title: "Our Highlights",
    home_relevantes_subtitle: "Top picks for you",
    home_intro_kicker: "WHO WE ARE",
    home_intro_title: "Link Education and Travel",
    home_intro_paragraph: "Since 2005, we have positioned ourselves internationally as a leading company in Mexico and Canada. We are constantly expanding, consolidating global alliances, and developing projects that connect cultures and opportunities.",
    home_intro_point_1: "End-to-end guidance before, during, and after each journey.",
    home_intro_point_2: "Educational and travel programs backed by international partners.",
    home_intro_point_3: "A global community that boosts your personal and professional growth.",
    home_intro_point_4: "Official representation of leading international education institutions.",
    home_intro_point_5: "Comprehensive guidance for students, families, and schools.",
    home_intro_end: "We specialize in academic mobility and travel, offering educational programs, cultural experiences, and journeys designed to transform lives.",
    home_intro_primary_cta: "Explore our story",
    home_intro_secondary_cta: "Book a consultation",
    home_intro_stat_years_value: "+20",
    home_intro_stat_countries_value: "+18",
    home_intro_stat_countries_label: "Countries represented",
    home_intro_stat_years_label: "Years creating experiences",
    home_intro_stat_students_value: "+15,000",
    home_intro_stat_students_label: "Transformed stories",
    home_intro_stat_partners_value: "100+",
    home_intro_stat_partners_label: "Educational Institutions",
  home_tour_step_scroll_intro: "Scroll down to uncover more about our services.",
  home_tour_step_select_category: "Pick the category you want to explore (Link Travel or Link Education).",
  home_tour_step_scroll_services: "Keep scrolling until you reach the services section to see what's available.",
  home_tour_step_select_service: "Click the service that grabs your attention to preview its details.",
  home_tour_step_view_more: "Finish by pressing “View more” to open the full service information.",
  home_tour_prompt_title: "New around here?",
  home_tour_prompt_body: "We can guide you with a quick walkthrough of the main sections on this page. Want to see it now?",
  home_tour_prompt_accept: "Yes, show me the tour",
  home_tour_prompt_decline: "No, thanks",
  home_tour_skip: "Skip",
  home_tour_manual_next: "Continue",
  home_what_we_offer_banner: "WHAT DO WE OFFER?",
  home_what_we_offer_title: "What do we offer?",
  home_packages_similar: "Similar packages",
  home_20_years: "20 years of experience",
  home_slogan_1: "Making your dreams happen",
  home_slogan_2: "Trust and commitment",
  home_slogan_3: "Connections that last",
  home_slogan_4: "Learn, travel, grow",
  call_cta: "Call",
  email_cta: "Email",
    home_offer_title: "What do we offer?",
    home_carousel_1: "20 years of experience",
    home_carousel_2: "Making your dreams happen",
    home_carousel_3: "Trust and commitment",
    home_carousel_4: "Connections that last",
    home_carousel_5: "Learn, travel, grow",
    home_connect_kicker: "// LET'S CONNECT",
    home_connect_title: "Achieving goals with LET",
    home_connect_paragraph: "At Grupo LET, we support you from start to finish: planning, educational and travel advising, visa and housing processing, and on-site support. We connect your project with accredited institutions, trusted partners, and a community that empowers you.",
    home_bullet_community: "Vibrant Community",
    home_bullet_advisory: "Comprehensive Advisory",
    home_bullet_experience: "Experience and Commitment",
    home_ceo_role: "CEO GRUPO LET",
    home_experience_years_label: "Years of experience",
    home_partners_label: "OUR PARTNERS",
    home_partners_title1: "Accreditations",
    home_partners_title2: "and strategic alliances",
    home_partners_paragraph: "At LET, we believe collaboration drives growth. These alliances and accreditations validate our practices and expand opportunities for our students.",
    home_offices_title: "Our Offices",
    home_offices_subtitle: "International presence to serve you wherever you are",
    subservice_label: "Sub-service",
    mapa_destinos_title: "Destinations Map",
    destinations_available: "Available Destinations",
    no_destinations_for_service: "No destinations for this service.",
    image_unavailable: "Image unavailable",
    suggested_activities: "Suggested Activities",
    select_destination_prompt: "Select a destination from the list to view details.",
    view_gallery: "View gallery",
    view_details: "View details",
    home_call_button: "Call",
    home_email_button: "Email",
    aria_llamar_a: "Call",
    aria_enviar_correo_a: "Send email to",
    home_reviews_title: "What our students say",
    home_review1_text: "Excellent service and support throughout the entire process. Highly recommended.",
    home_review2_text: "Exceptional personalized attention. They helped me achieve my dream of studying in Australia.",
    home_review3_text: "Committed professionals who truly care about their students' success.",
    home_reviews_word: "reviews",
    leave_google_review: "Leave a review on Google",
    loading_map: "Loading map...",
    loading_globe: "Loading 3D view...",
    globe_unavailable_title: "3D not available",
    globe_unavailable: "3D view requires extra dependencies. Install react-globe.gl and three.",
    // About page (About Us)
    about_section_title: "About us",
    about_who_we_are: "Who we are",
    about_card1_title: "LET | Link Education and Travel",
    about_card1_intro: "Since June 1, 2005, LET – Link Education and Travel has been a specialized group in academic mobility programs and tourism, positioned as a leader in the international education and travel industry.",
    about_card1_programs_title: "Programs that transform lives",
    about_card1_programs_item1: "Summer and winter camps for children and youth.",
    about_card1_programs_item2: "Specialized tourism and leisure travel.",
    about_card1_programs_item3: "Official representation of Canadian universities in Mexico.",
    about_card1_programs_item4: "Comprehensive advisory and support for students, families and institutions.",
    about_card2_eyebrow: "Expansion & Vision",
    about_card2_title: "Global presence",
    about_card2_p1: "In 2018, LET opened offices and direct operations in Canada to strengthen ties, provide local support to students and institutions, and expand international projects.",
    about_card2_p2: "Today, LET projects itself as a growing global group with strategic alliances in Latin America, Europe and Asia, creating bridges that unite opportunities and transform lives through education, culture and tourism.",
  about_card2_p3: "Supported by a multicultural team and active participation in major educational and tourism associations in Mexico and Canada, LET continues to lead international mobility with innovation, integrity and human commitment.",
  about_history_subtitle: "Milestones that have marked our path.",
  about_mission_vision_title: "Mission & Vision",
  about_mission_label: "Mission",
  about_mission_title: "Support you in every process and trip",
  about_mission_text: "Provide reliable and agile solutions in procedures and travel services, offering personalized attention and measurable results that inspire confidence at every step.",
  about_vision_label: "Vision",
  about_vision_title: "Be your strategic ally for 20+ more years",
  about_vision_text: "Consolidate as a national reference in comprehensive services for travelers and organizations, innovating with technology, operational excellence and a people-centered culture.",
  about_values_title: "Our Values",
  about_values_honestidad_title: "Honesty",
  about_values_honestidad_text: "We act with transparency and sincerity in every relationship and decision.",
  about_values_confianza_title: "Trust",
  about_values_confianza_text: "Clear processes, metrics and continuous improvement for superior results.",
  about_values_integracion_title: "Integration",
  about_values_integracion_text: "Transparency and compliance that sustain long-term relationships.",
  about_values_tolerancia_title: "Tolerance",
  about_values_tolerancia_text: "Fast response times with clear communication at every stage.",
  about_values_calidad_title: "Quality",
  about_values_calidad_text: "We work as strategic partners to achieve your goals.",
  about_overlay_years: "YEARS",
  about_overlay_phrase: "TRAVELING WITH YOU",
  nav_divisas: "Exchange Rate",
  nav_pagos: "Online Payment",
  nav_lets_study: "Link Education",
  nav_lets_study_desc: "Academic programs",
  nav_lets_travel: "Link Travel",
    nav_participations_desc : "Alliance and the latest",
    nav_participations: "Alliances",
    nav_visas: "Visas",
    nav_blog: "Blog",
    lets_study_children_languages: "Languages",
    lets_study_children_work_and_study: "Study & Work",
    lets_study_children_highschool: "High school",
    lets_study_children_aupair: "Au Pair",
    lets_study_children_pathway: "Pathway",
    lets_study_children_higher_education: "Higher Education",
    lets_study_children_online_english: "Online English",
    lets_study_children_educational_groups: "International Educational Groups",
    lets_study_children_expo: "Study Abroad Expo 2027",
    lets_travel_children_visa_americana: "American Visa",
    lets_travel_children_visa_canadiense: "Canadian Visa",
    lets_travel_children_international_travel: "International travel",
    lets_travel_children_domestic_travel: "Domestic travel",
    lets_travel_children_bachelor_parties: "Bachelor parties",
    lets_travel_children_honeymoons: "Honeymoons",
    lets_travel_children_cruises: "Cruises",
    lets_travel_children_quinceaneras: "Quinceañeras",
    lets_travel_children_destination_weddings: "Weddings",
    lets_travel_children_personalized_trips: "Personalized Trips",
  secondary_services_nav_aria: "Travel services",
  // Service descriptions added from user's copy (EN)
  lets_study_children_languages_desc: "Intensive courses to learn a language in destinations where it is natively spoken.",
  lets_study_children_work_and_study_desc: "Programs that combine studies with work experience abroad.",
  lets_study_children_higher_education_title: "Higher Education",
  lets_study_children_higher_education_desc: "Undergraduate, master's and doctoral programs at world-renowned universities.",
  lets_study_children_highschool_desc: "Programs designed to prepare students academically and linguistically for studying abroad.",
  lets_study_children_aupair_desc: "Live with a host family, care for their children, and enrich your cultural experience.",
  lets_study_children_visas: "migration procedures",  
  lang_toggle_aria: "Change language",
    menu_open_aria: "Open menu",
    menu_close_aria: "Close menu",
    social_facebook: "Facebook",
    social_instagram: "Instagram",
    social_youtube: "YouTube",
    social_linkedin: "LinkedIn",
    brand_alt: "Grupo LET",
  // Services & hero extras
  btn_see_more: "See more",
  btn_explore: "Explore",
  learn_more: "Learn more",
  quote_here: "Get a quote",
  wa_short: "WA",
  includes_flight: "Includes flight",
  view_itinerary: "View itinerary",
  services_relevantes_title: "Highlights",
  services_packages_similar: "Similar packages",
  services_why_choose_title: "Why choose us?",
  services_process_title: "Our process",
  services_faq_title: "Frequently Asked Questions",
  services_no_subservices: "No sub-services available right now.",
  // (deduplicated) factura_solicitar is defined earlier near the Exchange Rates section
  // Labels for editable fields
  service_label: "Service",
  description_label: "Description",
    // Jobs module (Careers)
    jobs_title_prefix: "JOB OPPORTUNITIES AT",
    jobs_title_highlight: "LET",
    jobs_subtitle: "Find the ideal position for you. Filter by location, department and job type.",
    jobs_search_placeholder: "Search by title, department or location",
    jobs_quick_all: "All",
    jobs_quick_remote: "Remote",
    jobs_quick_mexico: "Mexico",
    jobs_quick_canada: "Canada",
    jobs_quick_marketing: "Marketing",
    jobs_quick_operations: "Operations",
    jobs_filter_location: "Location",
    jobs_filter_location_all: "All",
    jobs_filter_location_mexico: "Mexico",
    jobs_filter_location_canada: "Canada",
    jobs_filter_location_australia: "Australia",
    jobs_filter_location_remote: "Remote",
    jobs_filter_department: "Department",
    jobs_filter_department_all: "All",
    jobs_filter_department_sales: "Sales",
    jobs_filter_department_operations: "Operations",
    jobs_filter_department_marketing: "Marketing",
    jobs_filter_department_legal: "Legal",
    jobs_filter_sort_by: "Sort by",
    jobs_sort_relevantes: "Most relevant",
    jobs_sort_recientes: "Most recent",
    jobs_sort_remoto: "Remote first",
    jobs_sort_sueldo: "Best salary",
    jobs_loading: "Loading…",
    jobs_results_word: "results",
    jobs_clear: "Clear",
    jobs_badge_featured: "Featured",
    jobs_badge_urgent: "Urgent",
    jobs_view_details: "View details",
    jobs_apply_now: "Apply now",
    jobs_load_more: "Load more",
    jobs_empty_title: "We couldn't find positions",
    jobs_empty_desc: "Try adjusting your search filters or send us your CV for future opportunities.",
    jobs_send_cv: "SEND CV",
    jobs_salary: "Salary",
    jobs_save: "Save",
    jobs_share: "Share",
    jobs_link_copied: "Link copied",
    jobs_dialog_apply_title: "Apply to the position",
    jobs_dialog_apply_vacancy: "Position",
    jobs_field_name: "Name",
    jobs_field_email: "Email",
    jobs_field_whatsapp: "WhatsApp",
    jobs_field_cvurl: "CV URL (Drive/Dropbox)",
    jobs_field_message_optional: "Message (optional)",
    jobs_send: "Send",
    jobs_application_sent: "Application sent",
    jobs_application_error: "Error sending application",
    // Jobs Benefits & Culture
    jobs_benefits_title_prefix: "MORE THAN A",
    jobs_benefits_title_highlight: "JOB",
    jobs_benefits_subtitle: "A unique personal and professional growth experience",
    jobs_benefit_international_title: "International Experience",
    jobs_benefit_international_desc: "Work opportunities in Canada, Australia and other destinations",
    jobs_benefit_professional_title: "Professional Development",
    jobs_benefit_professional_desc: "Continuous training and international certifications",
    jobs_benefit_multicultural_title: "Multicultural Team",
    jobs_benefit_multicultural_desc: "Work with people from different countries and cultures",
    jobs_benefit_medical_title: "Premium Medical Insurance",
    jobs_benefit_medical_desc: "Comprehensive coverage for you and your family",
    jobs_benefit_bonus_title: "Performance Bonuses",
    jobs_benefit_bonus_desc: "Additional rewards based on your performance",
    jobs_benefit_flex_title: "Work Flexibility",
    jobs_benefit_flex_desc: "Flexible schedules and remote work options",
    jobs_culture_title_prefix: "OUR",
    jobs_culture_title_highlight: "VALUES",
    jobs_culture_subtitle: "What makes us unique as a team and company",
    jobs_value_passion_title: "Passion for Transforming Lives",
    jobs_value_passion_desc: "We believe in the power of international education to change paths and create opportunities. We’re motivated to help each person reach their full potential.",
    jobs_value_team_title: "Teamwork",
    jobs_value_team_desc: "We collaborate, support each other and grow together. One person’s success is everyone’s success. We foster open communication and mutual respect.",
    jobs_value_excellence_title: "Excellence and Quality",
    jobs_value_excellence_desc: "We strive for excellence in every detail, from the first consultation to follow-up. We always aim to exceed expectations.",
    jobs_value_diversity_title: "Diversity and Inclusion",
    jobs_value_diversity_desc: "We value and respect cultural and personal differences. We believe diversity enriches us and makes us better.",
    jobs_value_integrity_title: "Integrity",
    jobs_value_integrity_desc: "We act with honesty and professional ethics in every process. The trust of our students and colleagues is our priority.",
    jobs_value_innovation_title: "Innovation",
    jobs_value_innovation_desc: "We drive new ideas to improve the experience of our students and colleagues. We adapt to change and seek creative solutions.",
    jobs_cta_title_prefix: "READY TO",
    jobs_cta_title_highlight: "GET STARTED?",
    jobs_cta_paragraph: "Join a team that’s transforming lives and building futures. Your next professional adventure awaits.",
    jobs_step_simple_process: "Simple application process",
    jobs_step_48h_response: "Response within 48 hours",
    jobs_step_team_interviews: "Interviews with the team",
    jobs_step_onboarding: "Personalized onboarding",
    jobs_btn_send_my_cv: "SEND MY CV",
    jobs_btn_contact_hr: "CONTACT HR",
    
    // Australia page (EN)
    auss_meta_title: "LET - Viva Australia | Boost Your Future in Australia",
    auss_whatsapp_text: "Hi! I'd like to learn more about your services",
    auss_whatsapp_tooltip: "Chat with us!",
    auss_hero_title: "Boost Your Future in Australia",
    auss_hero_tagline: "An Alliance by and for Mexican Talent",
    auss_hero_subtitle: "Discover how LET and VIVA Australia work together to open the door to unforgettable work experiences in a country full of opportunities.",
    auss_cta_more: "I want to know more!",
    
    auss_alliance_badge: "Formal Strategic Alliance",
    auss_alliance_title: "An Alliance by and for Mexican Talent",
    auss_alliance_subtitle: "This collaboration arises from our shared goal to offer comprehensive solutions to young Mexicans seeking work experience abroad, with reliable support, personalized guidance and ongoing accompaniment.",
    auss_alliance_h3: "Why this alliance?",
    auss_alliance_h4: "A bridge between Mexico and Australia",
    auss_alliance_p1_bold: "Complementary services:",
    auss_alliance_p1_rest: " LET provides strong expertise in advising and preparation for destinations like Australia, while Viva Australia reinforces the legal and immigration side with specialized experience in visas, regulations, and representation before Australian authorities.",
    auss_alliance_p2_bold: "All-in-one guidance:",
    auss_alliance_p2_rest: " Thanks to this alliance, recent graduates, soon-to-be graduates and professionals can access a 100% integrated solution.",
    auss_alliance_p3_bold: "Trust and professional backing:",
    auss_alliance_p3_rest: " Both organizations hold key accreditations such as MARA, MIA, QEAC, CRICOS and alliances with recognized universities and institutions, ensuring every process is handled transparently and in line with current law.",
    auss_alliance_p4_bold: "Regional coverage and closeness:",
    auss_alliance_p4_rest: " With presence across Latin America and Australia, this alliance offers close support before and during travel, with local teams who understand both the origin context and the destination country.",
    auss_youtube_alt: "Viva Australia & LET Alliance",
    
    auss_benefits_badge: "Alliance Objectives",
    auss_benefits_title: "Exclusive Benefits of Our Alliance",
    auss_benefits_subtitle: "Each advantage is designed so your project in Australia becomes more than a dream: a strategic, successful reality that propels Mexican talent toward a global future.",
    auss_benefit1_title: "Your Success, Our Guidance",
    auss_benefit1_desc: "Get expert, personalized guidance that maps out your professional development in Australia.",
    auss_benefit2_title: "Travel with Total Peace of Mind",
    auss_benefit2_desc: "Enjoy 360° support—from visas to adaptation—removing any worries.",
    auss_benefit3_title: "Boost Your Global Career",
    auss_benefit3_desc: "Access countless professional opportunities in one of the world’s most dynamic economies.",
    auss_benefit4_title: "The Power of Two Experts",
    auss_benefit4_desc: "The combined experience of LET and VIVA Australia, two internationally trusted agencies.",
    
    auss_partners_badge: "Get to know us",
    auss_partners_title: "Who We Are",
    auss_partners_subtitle: "Two leading organizations united by a common goal: making the professional dreams of young Mexicans in Australia come true.",
    auss_partner_let_slogan: "\"We are your way to reach the world\"",
    auss_partner_let_desc: "Grupo LET is a Mexican-Canadian company specialized in educational and leisure tourism. Founded in Toluca in 2005. We have been part of more than 15,347 experiences and dreams come true, maintaining quality and professionalism in our services.\n\nCommitment: Strengthen our purpose as an institution by opening global paths through training, guidance and international mobility.",
    auss_partner_viva_slogan: "\"Make your life project in Australia a reality\"",
    auss_partner_viva_desc: "VIVA Australia is an Australian agency with presence across Latin America and Australia since 1999, specialized in migration and education. We offer high-level professional guidance for those seeking an academic or work experience in Australia.\n\nCommitment: Guide Latin students to quality study programs and help professionals practice their careers in the country.",
    
    auss_slider1_title: "Your future in Australia starts today",
    auss_slider1_desc: "Don’t miss the chance to discover how our LET–Viva Australia alliance can open doors to transformative experiences. Our team is ready to provide the guidance you need to take your first step toward an Australian future.",
    auss_slider1_cta: "Schedule Free Guidance",
    auss_slider2_title: "Australia: Where your professional dreams come true.",
    auss_slider2_desc: "With the backing of LET and Viva Australia, your ambition to grow and shine in the international job market finds its path. This country offers the perfect stage to build the career you’ve always imagined, surrounded by innovation and unparalleled opportunities.",
    auss_slider2_cta: "Explore Opportunities",
    
    auss_target_badge: "For You",
    auss_target_title: "Is this your opportunity?",
    auss_target_h2: "Who is this opportunity for?",
    auss_target_h5: "This program is for you if:",
    auss_target_item1: "You want to do professional internships in Australia",
    auss_target_item2: "You need to improve your English",
    auss_target_item3: "You want to develop global skills in your professional area",
    auss_target_item4: "You want to transform your life",
    auss_target_item5: "You want international work experience",
    auss_target_item6: "You want to explore new cultures",
    
    auss_contact_title: "Start Your Australian Adventure!",
    auss_contact_subtitle: "Fill out the form and receive free personalized guidance to plan your future in Australia.",
  },
  FR: {
  calculator_title: "Calculatrice de conversion de devises",
  amount_label: "Quantité",
  from_label: "de",
  tipo_cambio_conoce_mas: "découvre plus",
  lets_travel_children_corporate_travel: "Voyages d'affaires",
  Pagos_title: "Effectuez votre paiement en ligne.",
  // Pagos / Payment page
  "pagos.client_title": "Données du client",
  "pagos.client_name_label": "Nom complet",
  "pagos.client_email_label": "E-mail",
  "pagos.client_phone_label": "Téléphone",
  "pagos.client_country_label": "Pays",
  "pagos.service_category_label": "Catégorie de service",
  "pagos.service_select_label": "Service à payer",
  "pagos.currency_label": "Devise",
  "pagos.amount_label": "Montant (sans centimes)",
  "pagos.card_data_title": "Détails de la carte",
  "pagos.card_holder_label": "Titulaire (tel qu'indiqué sur la carte)",
  "pagos.card_number_label": "Numéro de la carte",
  "pagos.card_exp_month_label": "Mois d'expiration",
  "pagos.card_exp_year_label": "Année d'expiration",
  "pagos.card_cvv_label": "CVV",
  "pagos.note_currency_hint": "* Nous suggérons la devise selon le pays, mais vous pouvez la changer avant de payer.",
  "pagos.canada_surcharge_note": "* Des frais de traitement de 4 % s'appliquent au Canada (équivalent à {surcharge} {currency}).",
  "pagos.pay_button": "Payer",
  "pagos.processing": "Traitement...",
  "pagos.privacy_notice_link": "Voir l'avis de confidentialité",
  "pagos.payment_error_title": "Le paiement n'a pas pu être traité",
  "pagos.close": "Fermer",
  // Receipt / Pago confirmado
  "pagos.badge_receipt": "Reçu numérique",
  "pagos.confirmed_title": "Votre paiement est confirmé",
  "pagos.no_recent_payment_title": "Aucun paiement récent trouvé",
  "pagos.confirmed_description": "Téléchargez votre ticket avec les détails complets et partagez la confirmation avec votre conseiller Link.",
  "pagos.no_recent_payment_description": "La session actuelle n'a pas de paiement confirmé. Revenez au formulaire pour en enregistrer un nouveau.",
  "pagos.download_ticket": "Télécharger le ticket PDF",
  "pagos.another_payment": "Effectuer un autre paiement",
  "pagos.no_payment_heading": "Aucun paiement récent",
  "pagos.no_payment_description": "Nous n'avons trouvé aucun paiement confirmé à afficher. Revenez au formulaire pour réessayer.",
  "pagos.go_to_payments": "Aller aux paiements",

  // PDF specific
  "pagos.pdf_title": "Reçu de paiement",
  "pagos.pdf_subtitle": "Grupo LET | Opéré par OpenPay",
  "pagos.pdf_amount_paid": "MONTANT PAYÉ",
  "pagos.pdf_status": "STATUT",
  "pagos.pdf_transaction": "TRANSACTION",
  "pagos.pdf_details": "DÉTAILS DU PAIEMENT",
  "pagos.pdf_observations": "Observations",
  "pagos.pdf_obs_line1": "Ce reçu confirme que le paiement a été traité via OpenPay.",
  "pagos.pdf_obs_line2": "Conservez ce document pour toute clarification.",
  "pagos.pdf_contact": "Contact : +52 722 235 2370 · WhatsApp +52 712 231 5859",
  "pagos.pdf_address_line1": "Adresse : Calle Andres Quintana Roo #529, Col. Francisco Murguia, El Ranchito,",
  "pagos.pdf_address_line2": "C.P. 50130, Toluca, Mexique",
  "pagos.pdf_logo_error_title": "ERREUR : Impossible de charger le logo SVG",
  "pagos.pdf_logo_error_details": "Vérifiez la console du navigateur pour les détails.",
  "pagos.verifying_openpay": "Vérification avec OpenPay, un instant...",
  "pagos.openpay_status_report": "Statut rapporté par OpenPay : {status}. Si le paiement est terminé, contactez le support.",
  "pagos.openpay_verify_error": "Impossible de vérifier le statut du paiement.",
  "pagos.download_error": "Impossible de générer le ticket. Réessayez.",
  // Terms / Privacy Notice
  terms_toc_contents: "Contenu",
  terms_print: "Imprimer",
  terms_header_privacy_notice: "Avis de confidentialité",
  terms_intro_sentence: "Le site Internet grupo-let.com, exploité par Link Education & Travel (LET), est responsable de la collecte, de l'utilisation et de la protection de vos données personnelles.",
  terms_section1_title: "Contrôleur de données",
  terms_section1_body: "LET est le responsable du traitement des informations personnelles liées aux services éducatifs et de voyage proposés sur grupo-let.com.",
  terms_section2_title: "Collecte de données personnelles",
  terms_section2_intro: "Nous pouvons demander, le cas échéant, les documents suivants pour traiter vos services :",
  terms_section2_item1: "Formulaires auprès du conseiller pédagogique ou du site Internet.",
  terms_section2_item2: "Preuve de paiement et documents de service associés.",
  terms_section2_item3: "Informations permettant de vérifier l'identité du demandeur.",
  terms_section2_item4: "Documents de propriété (si requis par la procédure).",
  terms_section2_item5: "Factures pour valider les paiements et les transactions associées.",
  terms_section3_title: "Utilisation des données personnelles",
  terms_section3_item1: "Vérifiez l'identité du demandeur.",
  terms_section3_item2: "Traiter les candidatures et gérer les procédures d'éducation et de voyage.",
  terms_section3_item3: "Gérez les relations avec les utilisateurs et tenez-les informés.",
  terms_section3_item4: "Gérer les frais et les paiements liés à nos services.",
  terms_section3_note_summary: "Remarque sur des objectifs supplémentaires",
  terms_section3_note_body: "Si nous avons l'intention d'utiliser vos données à des fins autres que celles indiquées, nous vous en informerons et, le cas échéant, vous demanderons votre consentement.",
  terms_section4_title: "Liens tiers",
  terms_section4_body: "Ce site peut contenir des liens vers des sites Internet de tiers. LET n'est pas responsable de leurs pratiques de confidentialité ou de leur contenu. Nous vous recommandons de revoir leurs politiques.",
  terms_section5_title: "Mises à jour des politiques",
  terms_section5_body: "Nous révisons périodiquement nos pratiques et mettons à jour cette politique. Veuillez le vérifier régulièrement pour les changements.",
  terms_section6_title: "Google Analytics et remarketing",
  terms_section6_body_part1: "Ce site utilise Google Analytics et la publicité display (Remarketing). Plus d'informations sur",
  terms_section6_body_part2: "Vous pouvez gérer vos préférences en matière d'annonces Google.",
  terms_section7_title: "Questions et commentaires",
  terms_section7_intro: "Si vous avez des questions sur cette politique ou nos pratiques, contactez-nous :",
  terms_section7_phone: "Téléphone",
  terms_section7_address: "Adresse",
  terms_section8_title: "Confidentialité et sécurité",
  terms_section8_body: "LET préserve la confidentialité des données personnelles et applique des mesures de sécurité raisonnables pour les protéger contre tout accès non autorisé, utilisation abusive ou divulgation.",
  terms_section9_title: "Paiements",
  terms_section9_body: "Les paiements seront traités via la passerelle Openpay.",
  terms_last_update: "Dernière mise à jour :",
  terms_last_update_date: "15 septembre 2025",
  terms_skip_to_content: "Passer au contenu",
  terms_toc_label: "Table des matières",
  terms_anchor_aria: "Lien permanent",
  terms_print_aria: "Imprimer",
  terms_back_to_top: "Retour en haut",
  // Admin menu and dashboard
  admin_dashboard: "Tableau de bord Admin",
  admin_choose_section: "Choisissez une section :",
  admin_menu_divisas: "Devises",
  admin_menu_blog_posts: "Blog - Articles",
  admin_menu_blog_articles: "Articles du blog",
  admin_menu_blog_trending: "Tendances du blog",
  admin_menu_testimonials: "Témoignages",
  admin_menu_popups: "Popups",
  admin_label: "Admin",
  admin_items: "éléments",
  admin_configurations: "Configurations",
  admin_common_active: "Actif",
  admin_common_inactive: "Inactif",
  admin_common_saved: "Enregistré",
  admin_common_save_error: "Erreur lors de l'enregistrement",
  admin_common_delete_error: "Erreur lors de la suppression",
  admin_common_update: "Mettre à jour",
  admin_common_create: "Créer",
  admin_common_add: "Ajouter",
  // Blog posts
  admin_blog_posts: "Articles de blog",
  admin_blog_no_posts: "Aucun message.",
  admin_blog_select_new_or_edit_post: "Cliquez sur \\\"Nouveau\\\" ou choisissez une publication à modifier.",
  admin_post_kind_blog: "Blogue",
  admin_post_kind_noticias: "Nouvelles",
  admin_post_kind_webinars: "Webinaires",
  // Promotions
  admin_promotions: "Promotions",
  admin_promos_manage_desc: "Gérez les promotions avec une sécurité basée sur des jetons",
  admin_promos_id_title_required: "Une pièce d'identité et un titre sont requis",
  admin_token_persistence: "Stocké dans le cookie \\\"admin_token\\\" et localStorage.",
  admin_save_token: "Enregistrer le jeton",
  admin_promos_description_ph: "Décrivez la promotion. Vous pouvez utiliser le formatage HTML, le gras, les listes, etc.",
  promos_filter_title: "Filtrer les promotions",
  promos_filter_description: "Sélectionnez une catégorie pour afficher uniquement les promotions correspondantes.",
  admin_field_subtitle: "Sous-titre",
  admin_field_price_original: "Prix ​​d'origine",
  admin_field_price_final: "Prix ​​final",
  admin_field_currency: "Devise",
  admin_field_discount: "% Rabais",
  admin_field_validity: "Validité",
  admin_field_badge: "Badge",
  admin_field_package_link: "Lien du paquet",
  admin_field_whatsapp: "WhatsApp",
  admin_field_travel_dates: "Dates de voyage",
  admin_promos_none: "Aucune promotion",
  admin_promos_confirm_delete: "Supprimer cette promotion ?",
  // Popup
  admin_popup_confirm_delete: "Supprimer cette popup ?",
  admin_field_cta_label: "Étiquette CTA",
  admin_field_cta_link: "Lien CTA",
  admin_field_allow_close: "Autoriser la fermeture",
  admin_field_paths: "Chemins (séparés par des virgules)",
  admin_field_paths_ph: "/, /blog, /divisas, /blog*",
  admin_field_start_iso: "Démarrer (ISO)",
  admin_field_end_iso: "Fin (ISO)",
  admin_triggers: "Déclencheurs",
  admin_trigger_onload: "En charge",
  admin_trigger_exit_intent: "Intention de sortie",
  admin_field_delay_ms: "Délai (ms)",
  admin_field_scroll_percent: "Défilement (%)",
  admin_frequency: "Fréquence",
  admin_frequency_once: "Une fois par séance",
  admin_hide_preview: "Masquer l'aperçu",
  admin_show_preview: "Afficher l'aperçu",
  admin_more_info: "Plus d'informations",
  // Trending
  admin_blog_trending: "Tendances du blog",
  admin_trending_type_articulo: "Article",
  admin_trending_type_guia: "Guide",
  admin_trending_type_promocion: "Promotion",
  admin_trending_type_paquete: "Emballer",
  // Products admin
  admin_products: "Produits",
  admin_products_desc: "Configurer le contenu pour chaque produit/service",
  admin_products_required: "Le slug, le titre et la description sont requis",
  admin_products_selected_service: "Prestation sélectionnée",
  admin_products_subtitle_ph: "Sous-titre du produit/service. Vous pouvez utiliser HTML.",
  admin_products_hero_images: "Images de héros",
  admin_products_no_images_hint: "Aucune image : les valeurs par défaut par service seront utilisées.",
  admin_products_related_packages: "Forfaits associés (de type promotion)",
  admin_products_no_related: "Aucun package associé",
  admin_field_days: "Jours",
  admin_field_days_ph: "Nombre de jours",
  admin_field_nights: "Nuits",
  admin_field_nights_ph: "Nombre de nuits",
  admin_field_countries: "Pays",
  admin_field_countries_ph: "Nombre de pays",
  admin_field_cities: "Villes",
  admin_field_cities_ph: "Nombre de villes",
  admin_field_include_flight: "Comprend le vol",
  admin_field_airline_logo_url: "URL logo compagnie aérienne",
  admin_field_itinerary_pdf_url: "URL PDF itinéraire",
  admin_field_taxes_label: "Libellé des taxes",
  admin_products_form_url: "URL du formulaire (iframe)",
  admin_products_form_url_ph: "https://form.jotform.com/votre-form",
  admin_products_form_url_hint: "S'il est fourni et qu'il s'agit d'un service, nous remplacerons les boutons CTA finaux par cette iframe et le bouton héros ouvrira ce formulaire.",
  admin_products_reasons: "Raisons (Pourquoi nous choisir ?)",
  admin_products_reason_ph: "Décrivez cette raison. Vous pouvez utiliser HTML.",
  admin_products_no_items: "Aucun article",
  admin_products_steps: "Étapes (processus)",
  admin_products_step_ph: "Décrivez cette étape du processus. Vous pouvez utiliser HTML.",
  admin_products_hero_variant: "Variante de héros",
  admin_products_faq_q: "Question",
  admin_products_faq_a: "Répondre",
  admin_products_faq_a_ph: "Réponse détaillée. Vous pouvez utiliser du HTML, des listes, des liens, etc.",
  admin_products_faq_cat_general: "Général",
  admin_products_faq_cat_about: "À propos de nous",
  admin_products_faq_cat_travel: "Voyage",
  admin_products_faq_cat_insurance: "Assurance",
  admin_products_faq_cat_stay: "Rester",
  admin_products_faq_cat_docs: "Documentation et paperasse",
  admin_products_updated: "Mis à jour",
  admin_products_none: "Aucun produit",
  admin_products_confirm_delete: "Supprimer ce produit ?",
  // Rates
  admin_rates_title: "Taux de change",
  admin_rates_subtitle: "Télécharger les tarifs et consulter l'historique",
  admin_token_sent_header: "Envoyé comme en-tête x-admin-token pour les actions.",
  admin_rates_base: "Base",
  admin_rates_pair: "Paire (histoire)",
  admin_rates_history: "Histoire",
  admin_rates_latest_records: "Derniers enregistrements",
  admin_rates_save_btn: "Économisez des tarifs",
  admin_no_data: "Aucune donnée",
  // Jobs admin
  admin_jobs_title: "Emplois d'administrateur",
  admin_jobs_confirm_delete: "Supprimer cette tâche ?",
  admin_job_open: "Poste ouvert",
  admin_job_closed: "FERMÉ",
  admin_field_location: "Emplacement",
  admin_field_department: "Département",
  admin_field_experience: "Expérience",
  admin_field_salary: "Salaire",
  admin_field_image_url: "URL de l'image",
  admin_field_link: "Lien (href)",
  admin_field_link_ph: "/blog/mon-article ou https://...",
  admin_field_views: "Vues",
  admin_field_description: "Description",
  admin_field_requirements_ph: "Exigences (une par ligne)",
  admin_field_benefits_ph: "Avantages (un par ligne)",
  // Login
  admin_login_invalid: "Identifiants invalides",
  admin_login_too_many: "Trop de tentatives. Réessayez plus tard.",
  admin_login_error: "Erreur d'authentification",
  admin_login_attempts_left: "Tentatives restantes",
  admin_login_blocked_retry_in: "Bloqué. Réessayez dans",
  admin_login_enter: "Entrer",
  // Admin common
  admin_token_label: "Jeton d'administrateur",
  admin_token_placeholder: "Collez votre RATES_ADMIN_TOKEN",
  admin_common_new: "Nouveau",
  admin_common_edit: "Modifier",
  admin_common_delete: "Supprimer",
  admin_common_cancel: "Annuler",
  admin_common_save: "Enregistrer",
  admin_common_remove: "Retirer",
  admin_common_saving: "Enregistrement…",
  admin_common_reload: "Recharger",
  admin_common_refresh: "Rafraîchir",
  admin_common_view: "Voir",
  admin_common_no_results: "Aucun résultat",
  admin_common_no_image: "Aucune image",
  admin_common_no_title: "Sans titre",
  admin_common_no_date: "Pas de date",
  admin_common_no_message: "(aucun message)",
  admin_common_not_provided: "(non fourni)",
  admin_common_export_csv: "Exporter CSV",
  admin_common_unauthorized_or_error: "Non autorisé ou erreur. Vérifiez le token.",
  // Admin table labels
  admin_table_date: "Date",
  admin_table_vacancy: "Offre",
  admin_table_name: "Nom",
  admin_table_email: "Email",
  admin_table_whatsapp: "WhatsApp",
  admin_table_cv: "CV",
  admin_table_message: "Message",
  admin_table_status: "Statut",
  admin_table_actions: "Actions",
  admin_open: "Ouvrir",
  admin_open_cv: "Voir le CV",
  // Applications
  admin_applications_title: "Applications",
  admin_applications_search_ph: "Rechercher par nom, email ou poste vacant",
  admin_application_to: "Demande à",
  admin_app_status_nuevo: "nouveau",
  admin_app_status_revisando: "révision",
  admin_app_status_entrevista: "entretien",
  admin_app_status_oferta: "offre",
  admin_app_status_rechazado: "rejeté",
  admin_app_status_contratado: "embauché",
  // Testimonials
  admin_testimonials_label: "témoignages",
  admin_testimonial: "Témoignage",
  admin_testimonials_confirm_delete: "Supprimer ce témoignage ?",
  // Blog
  admin_blog_articles: "Articles",
  admin_blog_write_here: "Écrivez votre article ici...",
  admin_blog_missing_title_slug: "Titre ou slug manquant",
  admin_editor: "Éditeur",
  admin_editing: "Édition",
  admin_blog_select_new_or_edit: "Cliquez sur \\\"Nouveau\\\" ou sélectionnez un article à modifier.",
  admin_blog_view_article: "Voir l'article",
  admin_common_no_articles: "Aucun article.",
  // Fields
  admin_field_title: "Titre",
  admin_field_slug: "Limace",
  admin_field_slug_ph: "mon-article",
  admin_field_excerpt: "Extrait",
  admin_field_category: "Catégorie",
  admin_field_tags: "Balises (séparées par des virgules)",
  admin_field_author: "Auteur",
  admin_field_date: "Date",
  admin_field_read_time: "Lire l'heure",
  admin_field_service: "Service",
  admin_field_cover_image: "Image de couverture (URL)",
  admin_field_content: "Contenu",
  admin_field_css: "CSS (facultatif)",
  admin_field_css_ph: "/* Styles personnalisés pour cet article */",
  admin_field_js: "JavaScript (facultatif)",
  admin_field_js_ph: "// Code JS pour l'interactivité dans cet article",
  admin_field_video_url: "URL de la vidéo (YouTube ou similaire)",
  admin_field_thumbnail: "Miniature (URL facultative)",
  admin_field_subtitle_optional: "Sous-titre (facultatif)",
  admin_field_currency_hint: "Devise (MXN, USD)",
  admin_field_price_optional: "Prix ​​(facultatif)",
  admin_field_price_prefix: "Étiquette avant le prix",
  admin_field_price_suffix: "Étiquette après le prix",
  admin_field_whatsapp_link: "Lien WhatsApp",
  // Preview
  admin_preview: "Aperçu",
  admin_nothing_to_preview: "Rien à prévisualiser.",
  // Rich Text Editor
  admin_rte_placeholder: "Écrivez votre contenu ici...",
  admin_rte_edit: "Modifier",
  admin_rte_preview: "Aperçu",
  admin_rte_helper_html: "Vous pouvez utiliser du HTML : <strong>gras</strong>, <em>italique</em>, <h1>titres</h1>, <ul><li>listes</li></ul>, etc.",
  admin_rte_heading_1: "Titre 1",
  admin_rte_heading_2: "Titre 2",
  admin_rte_heading_3: "Titre 3",
  admin_rte_bold_title: "Audacieux",
  admin_rte_italic_title: "Italique",
  admin_rte_underline_title: "Souligner",
  admin_rte_bulleted_title: "Liste à puces",
  admin_rte_numbered_title: "Liste numérotée",
  admin_rte_link_title: "Lien",
  admin_rte_linebreak_title: "Saut de ligne",
    nav_nosotros: "À PROPOS",
    nav_destinos: "DESTINATION",
    nav_servicios: "SERVICES",
    nav_recursos: "RESSOURCES",
    nav_eventos: "ÉVÉNEMENTS",
    nav_consulta: "CONSULTATION GRATUITE",
    nav_promociones: "PROMOTIONS",
    submenu_historia: "Découvrez notre histoire",
    submenu_destinos_desc: "Pays et villes disponibles",
    submenu_servicios_desc: "Services que nous proposons",
    nav_lets_travel_desc : "Experiencias de viaje",
    nav_participations_desc : "Alianzas y lo mas nuevo",
    lets_inmigrate_children_canada: "Migration Canada",
    lets_travel_children_eta_uk: "AVE Royaume-Uni",
    lets_travel_children_eta_canadiense: "AVE Canada",
    lets_travel_children_other: "Autres visas",
    submenu_recursos_desc: "Guides et outils",
    submenu_eventos_desc: "Webinaires et salons",
    popular: "Populaire",
    nuevo: "Nouveau",
  llamar_ahora: "APPELEZ MAINTENANT",
  cta_formulario: "REGISTRE",
  // Route-specific CTA labels (used by CTASection to change button text per pathname)
  cta_formulario_home: "S'INSCRIRE",
  cta_enroll_now: "Inscrivez-vous maintenant",
  price_from_label: "À partir de",
  form_register_title: "Formulaire d'inscription",
  cta_formulario_divisas: "VÉRIFIER LE TAUX DE CHANGE",
  cta_formulario_study: "INTÉRESSÉ À ÉTUDIER",
    cta_titulo_1: "PRÊT À COMMENCER VOTRE",
    cta_titulo_resaltado: "AVENTURE ?",
    cta_parrafo: "Rejoignez des milliers de personnes qui ont transformé leur vie avec nos programmes.",
    cta_contactar: "CONTACTER UN CONSEILLER",
    footer_estudios: "Études à l'étranger",
    footer_tramites: "Procédures d'immigration et visas",
    footer_viajes: "Voyages touristiques",
    footer_grupos: "Groupes éducatifs internationaux",
    footer_nosotros: "À propos de nous",
    footer_comunidad: "Communauté",
    footer_tipo_cambio: "Taux de change",
    footer_sobre_nosotros: "À propos de nous",
    footer_sucursales: "Nos succursales",
    footer_bolsa: "Emplois",
    footer_blogs: "Blogues",
    footer_podcast: "Podcast",
    footer_acceso_padres: "Accès parents",
    footer_promociones: "Promotions",
    footer_viajes_internacionales: "Voyage international",
    footer_viajes_nacionales: "Voyage intérieur",
    footer_despedida: "Enterrements de vie de garçon",
    footer_lunas_miel: "Lunes de miel",
    footer_cruceros: "Croisières",
    footer_quinceaneras: "Quinceañeras",
    footer_idiomas: "Langues",
    footer_estudia_trabaja: "Étudier et travailler",
    footer_educacion_superior: "Enseignement supérieur",
    footer_highschool: "Lycée",
    footer_aupair: "Fille au pair",
    footer_pathway: "Chemin",
    footer_ingles_online: "Anglais en ligne",
    footer_americana: "Américain",
    footer_canada: "Canada",
    footer_toronto: "Toronto",
    footer_quebec: "Québec",
    footer_londres: "Londres",
    footer_dublin: "Dublin",
    footer_vancouver: "Vancouver",
    footer_avisopriv: "Avis de confidentialité",
    footer_terminos: "Termes et conditions",
    footer_arriba: "↑ Haut",
    footer_consultar: "Vérifiez >>",
    footer_copy: "Tous droits réservés.",
    hero_title_1: "NOUS SOMMES VOTRE MANIÈRE DE",
    hero_title_2: "ATTEINDRE LE MONDE",
    hero_desc: "Nous sommes une entreprise mexico-canadienne spécialisée dans le tourisme éducatif et de loisirs. Fondée à Toluca en 2005. Nous avons participé à plus de 15 347 expériences et rêves réalisés, en maintenant la qualité et le professionnalisme de nos services.",
    hero_hashtag: "#LiveApprendreGrow",
  home_intro_video_alt: "Expériences LET en action",
  podcast_hero_title: "UN PODCAST FAIT POUR VOUS",
  podcast_recent_episodes: "Épisodes récents",
  podcast_brand_label: "LET'S TALK",
  podcast_stats_error: "Impossible de charger les statistiques de la chaîne",
  podcast_impact_title: "Impact du Podcast",
  podcast_impact_subtitle: "Statistiques en temps réel de la chaîne YouTube",
  podcast_metric_videos: "Vidéos de la chaîne",
  podcast_metric_subscribers: "Abonnés",
  podcast_metric_views: "Vues de la chaîne",
  podcast_why_title: "Pourquoi nous écouter ?",
  podcast_why_subtitle: "Contenu sélectionné pour vous aider à prendre des décisions claires et avancer sans bruit",
  podcast_why_item1_title: "Production professionnelle",
  podcast_why_item1_desc: "Audio clair, structure efficace et montage axé sur la valeur pratique, sans remplissage.",
  podcast_why_item1_badge: "Qualité sonore",
  podcast_why_item2_title: "Contenu vérifié",
  podcast_why_item2_desc: "Informations à jour sur bourses, visas, démarches et opportunités avec des sources fiables.",
  podcast_why_item2_badge: "Rigueur",
  podcast_why_item3_title: "Histoires réelles",
  podcast_why_item3_desc: "Témoignages authentiques d'étudiants qui ont transformé leur vie à l'international.",
  podcast_why_item3_badge: "Inspiration",
  podcast_why_item4_title: "Approche actionnable",
  podcast_why_item4_desc: "Chaque épisode se termine par des étapes concrètes pour avancer immédiatement.",
  podcast_why_item4_badge: "Applicable",
  podcast_faq_q1: "À quelle fréquence publiez-vous de nouveaux épisodes ?",
  podcast_faq_a1: "Nous publions un épisode principal chaque mardi et de courts bonus certains jeudis avec des mises à jour ou des conseils rapides.",
  podcast_faq_a2: "Vous pouvez proposer un sujet ou inviter quelqu'un en nous envoyant un message WhatsApp ou via le formulaire de contact ; cliquez sur « Proposer un sujet » pour ouvrir le chat.",
  podcast_faq_a3: "Oui — si vous souhaitez être invité, envoyez une courte biographie et une idée de sujet via le bouton « Proposer un sujet » et notre équipe vous contactera.",
  podcast_faq_a4: "Les ressources sont liées dans la description de chaque épisode YouTube et sur la page de l'épisode. Demandez-les via le chat si nécessaire.",
  podcast_faq_a5: "Non — le podcast est gratuit à l'écoute.",
  podcast_faq_q2: "Comment proposer un sujet ou inviter quelqu'un ?",
  podcast_faq_q3: "Puis-je apparaître en tant qu'invité ?",
  podcast_faq_q4: "Où puis-je accéder aux ressources mentionnées ?",
  podcast_faq_q5: "Le podcast est-il payant ?",
  podcast_any_other_question: "Une autre question ?",
  podcast_write_to_us_cta: "Écrivez-nous et nous en ferons un épisode",
  podcast_suggest_topic: "PROPOSER UN SUJET",
  // Tipo de cambio (divisas)
  tipo_cambio_title_prefix: "TAUX DE CHANGE",
  tipo_cambio_title_highlight: "AUJOURD'HUI",
  tabs_recomendaciones: "Recommandations",
  tabs_facturacion: "Facturation",
  factura_electronica_title: "Facture électronique",
  factura_electronica_paragraph: "Demandez votre facture électronique rapidement et en toute sécurité.",
  factura_solicitar: "Demander la facture",
  need_help_title: "Besoin d'aide ?",
  copy_result: "Copier le résultat",
  // JoinChat / WhatsApp messages
  joinchat_label_open: "Ouvrir le chat",
  joinchat_pill_text: "Vous avez des questions ?",
  joinchat_root_message: "Salut Grupo LET 👋 Je suis sur votre site et j'aimerais plus d'informations. Je vous contacte depuis votre site Web.",
  joinchat_podcast_message: "Salut 👋 J'ai écouté votre podcast et j'ai quelques questions. Je vous contacte depuis votre site Web.",
  joinchat_promotions_message: "Salut 👋 J'ai vu vos promotions et je suis intéressé par plus de détails. Je vous contacte depuis votre site Web.",
  joinchat_category_study_message: "Bonjour 👋 Je suis intéressé à étudier à l'étranger et j'aimerais des conseils. Je vous contacte depuis votre site Web.",
  joinchat_category_travel_message: "Salut 👋 Je souhaite des informations sur les voyages et les forfaits. Je vous contacte depuis votre site Web.",
  joinchat_tipo_cambio_message: "Salut 👋 J'ai des questions sur le taux de change, pouvez-vous m'aider ? Je vous contacte depuis votre site Web.",
  // Service-specific JoinChat messages
  joinchat_service_idiomas_message: "Salut 👋 Je suis intéressé par les cours de langues — pouvez-vous partager plus d'informations ? Je vous contacte depuis votre site Web.",
  "joinchat_service_work-and-study_message": "Hola 👋 Me interesa el programa de estudia y trabaja, ¿cómo funciona? Vengo de su página web.",
  "joinchat_service_educacion-superior_message": "Hola 👋 Quisiera información sobre educación superior y opciones de universidades. Vengo de su página web.",
  joinchat_service_highschool_message: "Salut 👋 Je souhaite en savoir plus sur les programmes du lycée. Je vous contacte depuis votre site Web.",
  joinchat_service_aupair_message: "Salut 👋 Je suis intéressé par le programme Au Pair — quelles sont les conditions ? Je vous contacte depuis votre site Web.",
  joinchat_service_nacionales_message: "Bonjour 👋 J'aimerais des informations sur les forfaits de voyages intérieurs. Je vous contacte depuis votre site Web.",
  joinchat_service_internacionales_message: "Bonjour 👋 Les voyages internationaux m'intéressent. Quelles destinations proposez-vous ? Je vous contacte depuis votre site Web.",
  joinchat_service_quince_message: "Salut 👋 Je veux des informations sur les voyages pour les 15 ans. Je vous contacte depuis votre site Web.",
  joinchat_service_despedidas_message: "Salut 👋 Je cherche à organiser un enterrement de vie de garçon, quelles options avez-vous ? Je vous contacte depuis votre site Web.",
  joinchat_service_grupos_message: "Salut 👋 J'ai besoin d'informations sur les déplacements en groupe et la coordination. Je vous contacte depuis votre site Web.",
  joinchat_service_bodas_message: "Salut 👋 J'aimerais connaître les destinations de mariage et les forfaits disponibles. Je vous contacte depuis votre site Web.",
  joinchat_service_personalizado_message: "Salut 👋 Je suis intéressé par un voyage sur mesure, comment les organisez-vous ? Je vous contacte depuis votre site Web.",
  // WhatsApp UI labels
  whatsapp_button: "WhatsApp",
  whatsapp_title: "WhatsApp",
  // small labels
  joinchat_close: "Fermer",
    services_title: "NOS SERVICES",
    services_subtitle: "Découvrez les opportunités que nous avons pour vous. Chaque programme est conçu pour offrir une expérience unique et transformatrice.",
  participaciones_hero_title: "Réalisez vos rêves",
  participaciones_hero_subtitle: "Réaliser vos objectifs avec LET",
  participaciones_experience_caption: "Plus de 15 ans d'expérience au service des rêves internationaux",
  participaciones_stat_students: "Étudiants ayant réussi",
  participaciones_stat_countries: "Pays de destination",
  participaciones_stat_success: "Taux de réussite",
  participaciones_stat_institutions: "Institutions",
  participaciones_process_title: "Votre processus en 4 étapes",
  participaciones_process_subtitle: "Un processus simple et transparent pour concrétiser votre rêve international",
  participaciones_step_1_title: "Consultation gratuite",
  participaciones_step_1_desc: "Nous évaluons votre profil et définissons le meilleur programme pour vous",
  participaciones_step_2_title: "Demande",
  participaciones_step_2_desc: "Nous vous aidons avec toute la documentation et le processus de visa",
  participaciones_step_3_title: "Préparation",
  participaciones_step_3_desc: "Nous organisons l'hébergement, les vols et tout le nécessaire",
  participaciones_step_4_title: "Support sur place",
  participaciones_step_4_desc: "Nous vous accompagnons tout au long de votre séjour",
  participaciones_faq_title: "Questions fréquemment posées",
  participaciones_faq_q1: "Quels services LET propose-t-il ?",
  participaciones_faq_a1: "Nous offrons des services spécialisés en éducation internationale, y compris des conseils pédagogiques, le traitement des visas, l'hébergement et un soutien complet pendant votre séjour à l'étranger.",
  participaciones_faq_q2: "Combien de temps dure le processus de visa étudiant ?",
  participaciones_faq_a2: "Les délais varient selon le pays et le type de visa ; le processus peut durer de quelques semaines à plusieurs mois. Nous recommandons de commencer tôt et de vérifier les exigences spécifiques du pays de destination.",
  participaciones_faq_q3: "Puis-je travailler pendant mes études ?",
  participaciones_faq_a3: "Cela dépend du visa et du pays. De nombreux visas étudiants permettent le travail à temps partiel ; nous vous conseillons sur les règles et les limites afin que vous puissiez combiner études et travail légalement.",
  participaciones_faq_q4: "Offrez-vous un suivi une fois que je suis sur place ?",
  participaciones_faq_a4: "Oui. Nous offrons un soutien continu et un suivi pendant votre séjour, y compris une aide pour l'hébergement, l'intégration et tout problème éventuel.",
    services_study_about: "Une opportunité complète de développer l'autonomie et l'indépendance tout en renforçant les compétences académiques, émotionnelles et sociales.",
    services_travel_about: "Explorez des destinations fascinantes tout en apprenant et en grandissant. Des voyages éducatifs conçus pour élargir votre vision du monde.",
    services_work_about: "Des opportunités de travail au Canada qui combinent emploi et apprentissage, développant des compétences professionnelles dans un environnement international.",
    stats_experiencias: "Expériences terminées",
    stats_anios: "Années d'expérience",
    stats_satisfaccion: "Satisfaction garantie",
    testimonials_title: "HISTOIRES DE SUCCÈS",
    testimonials_subtitle: "Découvrez des histoires vraies d'étudiants qui ont transformé leur vie grâce à nos programmes",
    testimonial_1_role: "Lien Étudiant en éducation",
    testimonial_1_content: "Grupo LET a complètement changé ma vie. Le programme d'études au Canada a ouvert des portes que je n'aurais jamais imaginées. Le soutien a été incroyable dès le premier jour.",
    testimonial_2_role: "Participant au travail de lien",
    testimonial_2_content: "Travailler au Canada tout en apprenant l'anglais était inestimable. J'ai désormais un avenir professionnel solide et un niveau d'anglais avancé.",
    testimonial_3_role: "Lien Voyageur voyageur",
    testimonial_3_content: "Les voyages éducatifs de LET sont plus que du tourisme. J'ai tellement appris sur la culture canadienne tout en améliorant mon anglais. Expérience inoubliable!",
    home_relevantes_title: "Nos essentiels",
    home_relevantes_subtitle: "Meilleurs choix pour vous",
    home_intro_kicker: "QUI NOUS SOMMES",
    home_intro_title: "Link Education and Travel",
    home_intro_paragraph: "Depuis 2005, nous sommes une entreprise internationale spécialisée dans le tourisme éducatif et les voyages. Nous consolidons des partenariats mondiaux et développons des projets qui rapprochent les cultures et créent des opportunités.",
    home_intro_point_1: "Un accompagnement de bout en bout avant, pendant et après chaque voyage.",
    home_intro_point_2: "Programmes éducatifs et de voyages soutenus par des partenaires internationaux.",
    home_intro_point_3: "Une communauté mondiale qui stimule votre croissance personnelle et professionnelle.",
    home_intro_point_4: "Représentation des institutions éducatives internationales.",
    home_intro_point_5: "Asesoría intégrale pour les étudiants, les familles et les écoles.",
    home_intro_end: "Nous sommes spécialisés dans la mobilité académique et le tourisme, offrant des programmes éducatifs, des expériences culturelles et des voyages conçus pour transformer les vies.",
    home_intro_primary_cta: "Explorez notre histoire",
    home_intro_secondary_cta: "Réservez une consultation",
    home_intro_stat_years_value: "+20",
    home_intro_stat_years_label: "Des années à créer des expériences",
    home_intro_stat_countries_value: "+18",
    home_intro_stat_countries_label: "Pays représentés",
    home_intro_stat_students_value: "+15 000",
    home_intro_stat_students_label: "Des histoires transformées",
    home_intro_stat_partners_value: "100+",
    home_intro_stat_partners_label: "Établissements d'enseignement",
  home_tour_step_scroll_intro: "Faites défiler vers le bas pour en savoir plus sur nos services.",
  home_tour_step_select_category: "Choisissez la catégorie que vous souhaitez explorer (Link Travel ou Link Education).",
  home_tour_step_scroll_services: "Continuez à faire défiler jusqu'à ce que vous atteigniez la section des services pour voir ce qui est disponible.",
  home_tour_step_select_service: "Cliquez sur le service qui retient votre attention pour prévisualiser ses détails.",
  home_tour_step_view_more: "Terminez en appuyant sur « Afficher plus » pour ouvrir les informations complètes sur le service.",
  home_tour_prompt_title: "Nouveau par ici ?",
  home_tour_prompt_body: "Nous pouvons vous guider avec une présentation rapide des principales sections de cette page. Vous voulez le voir maintenant ?",
  home_tour_prompt_accept: "Oui, montre-moi la visite",
  home_tour_prompt_decline: "Non merci",
  home_tour_skip: "Sauter",
  home_tour_manual_next: "Continuer",
  home_what_we_offer_banner: "QUE PROPOSONS-NOUS ?",
  home_what_we_offer_title: "Que vous offrons-nous ?",
  home_packages_similar: "Forfaits similaires",
  home_20_years: "20 ans d'expérience",
  home_slogan_1: "Réaliser vos rêves",
  includes_flight: "Inclut le vol",
  view_itinerary: "Voir l'itinéraire",
  home_slogan_2: "Confiance et engagement",
  home_slogan_3: "Des connexions qui durent",
  home_slogan_4: "Apprendre, voyager, grandir",
  call_cta: "Appel",
  email_cta: "Email",
    home_offer_title: "Que proposons-nous ?",
    home_carousel_1: "20 ans d'expérience",
    home_carousel_2: "Réaliser vos rêves",
    home_carousel_3: "Confiance et engagement",
    home_carousel_4: "Des connexions qui durent",
    home_carousel_5: "Apprendre, voyager, grandir",
    home_connect_kicker: "// CONNECTONS-NOUS",
    home_connect_title: "Atteindre ses objectifs avec LET",
    home_connect_paragraph: "Chez Grupo LET, nous vous accompagnons du début à la fin : planification, conseils pédagogiques et de voyage, traitement des visas et du logement, et assistance sur place. Nous connectons votre projet avec des institutions accréditées, des partenaires de confiance et une communauté qui vous responsabilise.",
    home_bullet_community: "Communauté dynamique",
    home_bullet_advisory: "Conseil complet",
    home_bullet_experience: "Expérience et engagement",
    home_ceo_role: "PDG GRUPO LET",
    home_experience_years_label: "Années d'expérience",
    home_partners_label: "NOS PARTENAIRES",
    home_partners_title1: "Accréditations",
    home_partners_title2: "et partenariats stratégiques",
    home_partners_paragraph: "Ces alliances et accréditations soutiennent nos pratiques et ouvrent des opportunités pour nos étudiants.",
    home_offices_title: "Nos agences",
    home_offices_subtitle: "Présence internationale pour vous servir où que vous soyez",
    home_call_button: "Appel",
    home_email_button: "E-mail",
    aria_llamar_a: "Appel",
    aria_enviar_correo_a: "Envoyer un e-mail à",
    home_reviews_title: "Ce que disent nos étudiants",
    home_review1_text: "Service excellent et accompagnement tout au long du processus. Très recommandé.",
    home_review2_text: "Attention personnalisée exceptionnelle. Ils m'ont aidé à réaliser mon rêve d'étudier en Australie.",
    home_review3_text: "Professionnels engagés et accompagnement de qualité.",
    home_reviews_word: "avis",
    leave_google_review: "Laisser un avis sur Google",
    loading_map: "Chargement de la carte...",
    loading_globe: "Chargement de la vue 3D...",
    globe_unavailable_title: "3D non disponible",
    globe_unavailable: "La vue 3D nécessite des dépendances supplémentaires. Installez react-globe.gl et three.",
    // About page (Nosotros)
    about_section_title: "À propos de nous",
    about_who_we_are: "Qui sommes-nous",
    about_card1_title: "LET | Link Education and Travel",
    about_card1_intro: "Depuis le 1er juin 2005, LET – Link Education and Travel s’est consolidé comme un groupe spécialisé dans les programmes de mobilité académique et le tourisme, reconnu comme un leader du secteur de l’éducation et du voyage à l’international.",
    about_card1_programs_title: "Des programmes qui transforment des vies",
    about_card1_programs_item1: "Camps d’été et d’hiver pour enfants et jeunes.",
    about_card1_programs_item2: "Tourisme spécialisé et voyages de loisirs.",
    about_card1_programs_item3: "Représentation officielle d’universités et écoles canadiennes au Mexique.",
    about_card1_programs_item4: "Conseil et accompagnement intégral pour étudiants, familles et institutions.",
    about_card2_eyebrow: "Expansion & Vision",
    about_card2_title: "Présence mondiale",
    about_card2_p1: "En 2018, LET a ouvert des bureaux et des opérations directes au Canada afin de renforcer les liens, offrir un accompagnement local aux étudiants et institutions, et développer des projets internationaux.",
    about_card2_p2: "Aujourd’hui, LET se projette comme un groupe mondial en croissance, avec des alliances stratégiques en Amérique latine, en Europe et en Asie, créant des ponts qui unissent les opportunités et transforment des vies par l’éducation, la culture et le tourisme.",
    about_card2_p3: "Soutenu par une équipe multiculturelle et une participation active aux principales chambres et associations éducatives et touristiques au Mexique et au Canada, LET continue de conduire la mobilité internationale avec innovation, intégrité et engagement humain.",
    about_history_subtitle: "Des jalons qui ont marqué notre parcours.",
    about_mission_vision_title: "Mission & Vision",
    about_mission_label: "Mission",
    about_mission_title: "Vous accompagner à chaque démarche et voyage",
    about_mission_text: "Fournir des solutions fiables et agiles en gestion de démarches et services de voyage, avec une attention personnalisée et des résultats mesurables qui inspirent confiance à chaque étape.",
    about_vision_label: "Vision",
    about_vision_title: "Être votre allié stratégique pour 20+ années de plus",
    about_vision_text: "Nous consolider comme la référence nationale en services intégrés pour voyageurs et organisations, en innovant avec la technologie, l’excellence opérationnelle et une culture centrée sur les personnes.",
    about_values_title: "Nos valeurs",
    about_values_honestidad_title: "Honnêteté",
    about_values_honestidad_text: "Nous agissons avec transparence et sincérité dans chaque relation et décision.",
    about_values_confianza_title: "Confiance",
    about_values_confianza_text: "Processus clairs, métriques et amélioration continue pour des résultats supérieurs.",
    about_values_integracion_title: "Intégration",
    about_values_integracion_text: "Transparence et conformité qui soutiennent des relations à long terme.",
    about_values_tolerancia_title: "Tolérance",
    about_values_tolerancia_text: "Délais de réponse rapides avec une communication claire à chaque étape.",
    about_values_calidad_title: "Qualité",
    about_values_calidad_text: "Nous travaillons comme partenaires stratégiques pour atteindre vos objectifs.",
    about_overlay_years: "ANS",
    about_overlay_phrase: "VOYAGEANT AVEC VOUS",
  nav_divisas: "Taux de change",
  nav_pagos: "Paiement en ligne",
  nav_lets_study: "Link Education",
  nav_lets_study_desc: "Programmes académiques",
  nav_lets_travel: "Link Travel",
  nav_participations: "Alliances",
  nav_visas: "Visas",
  nav_lets_inmigrate: "Immigrons",
  nav_lets_inmigrate_desc: "Procédures d'immigration",
  lets_inmigrate_australia: "Migration Australie",
  nav_blog: "Blogue",
  lets_study_children_languages: "Langues",
  lets_study_children_work_and_study: "Étudier et travailler",
  lets_study_children_highschool: "High School",
  lets_study_children_aupair: "Au Pair",
  lets_study_children_pathway: "Parcours préparatoire (Pathway)",
  lets_study_children_higher_education: "Enseignement supérieur",
  lets_study_children_online_english: "Anglais en ligne",
  lets_study_children_educational_groups: "Groupes éducatifs internationaux",
  lets_study_children_visas: "Tramites migrateurs",
  lets_study_children_expo: "Salon des Études à l'Étranger 2027",
  lets_travel_children_visa_americana: "Visa américain",
  lets_travel_children_visa_canadiense: "Visa canadien",
  lets_travel_children_international_travel: "Voyages internationaux",
  lets_travel_children_domestic_travel: "Voyages nationaux",
  lets_travel_children_bachelor_parties: "Enterrements de vie de célibataire",
  lets_travel_children_honeymoons: "Lunes de miel",
  lets_travel_children_cruises: "Croisières",
  lets_travel_children_quinceaneras: "Fêtes des 15 ans",
  lets_travel_children_destination_weddings: "Mariages",
  lets_travel_children_personalized_trips: "Voyages personnalisés",
  secondary_services_nav_aria: "Services de voyage",
  // Service descriptions added from user's copy
  lets_study_children_languages_desc: "Cours intensifs pour apprendre une langue dans des pays où elle est parlée nativement.",
  lets_study_children_work_and_study_desc: "Programmes qui combinent études et expérience professionnelle à l'étranger.",
  lets_study_children_higher_education_title: "Enseignement supérieur",
  lets_study_children_higher_education_desc: "Licences, masters et doctorats dans des universités de renommée internationale.",
  lets_study_children_highschool_desc: "Programmes conçus pour préparer académiquement et linguistiquement les élèves à l'étranger.",
  lets_study_children_aupair_desc: "Vivez chez une famille d'accueil, prenez soin des enfants et enrichissez votre expérience culturelle.",
  lang_toggle_aria: "Changer de langue",
  menu_open_aria: "Ouvrir le menu",
  menu_close_aria: "Fermer le menu",
  social_facebook: "Facebook",
  social_instagram: "Instagram",
  social_youtube: "YouTube",
  social_linkedin: "LinkedIn",
  brand_alt: "Groupe LET",
    // Services & hero extras
    btn_see_more: "Voir plus",
    btn_explore: "Explorer",
  learn_more: "En savoir plus",
  quote_here: "Demander un devis",
  wa_short: "WA",
    services_relevantes_title: "Points forts",
    services_packages_similar: "Forfaits similaires",
    services_why_choose_title: "Pourquoi nous choisir ?",
    services_process_title: "Notre processus",
    services_faq_title: "Questions fréquentes",
    services_no_subservices: "Aucun sous-service disponible pour le moment.",
    // Labels for editable fields
    service_label: "Service",
    subservice_label: "Sous-service",
    mapa_destinos_title: "Carte des destinations",
    destinations_available: "Destinations disponibles",
    no_destinations_for_service: "Aucune destination pour ce service.",
    image_unavailable: "Image non disponible",
    suggested_activities: "Activités suggérées",
    select_destination_prompt: "Sélectionnez une destination dans la liste pour voir les détails.",
    view_gallery: "Voir la galerie",
    view_details: "Voir les détails",
    description_label: "Description",
    // Jobs module (Bolsa de trabajo)
    jobs_title_prefix: "OPPORTUNITÉS D'EMPLOI CHEZ",
    jobs_title_highlight: "LAISSER",
    jobs_subtitle: "Trouvez le poste idéal pour vous. Filtrer par emplacement, département et type d'emploi.",
    jobs_search_placeholder: "Recherche par titre, département ou emplacement",
    jobs_quick_all: "Tous",
    jobs_quick_remote: "Télécommande",
    jobs_quick_mexico: "Mexique",
    jobs_quick_canada: "Canada",
    jobs_quick_marketing: "Commercialisation",
    jobs_quick_operations: "Opérations",
    jobs_filter_location: "Emplacement",
    jobs_filter_location_all: "Tous",
    jobs_filter_location_mexico: "Mexique",
    jobs_filter_location_canada: "Canada",
    jobs_filter_location_australia: "Australie",
    jobs_filter_location_remote: "Télécommande",
    jobs_filter_department: "Département",
    jobs_filter_department_all: "Tous",
    jobs_filter_department_sales: "Ventes",
    jobs_filter_department_operations: "Opérations",
    jobs_filter_department_marketing: "Commercialisation",
    jobs_filter_department_legal: "Légal",
    jobs_filter_sort_by: "Trier par",
    jobs_sort_relevantes: "Le plus pertinent",
    jobs_sort_recientes: "Le plus récent",
    jobs_sort_remoto: "À distance d'abord",
    jobs_sort_sueldo: "Meilleur salaire",
    jobs_loading: "Chargement...",
    jobs_results_word: "résultats",
    jobs_clear: "Clair",
    jobs_badge_featured: "En vedette",
    jobs_badge_urgent: "Urgent",
    jobs_view_details: "Afficher les détails",
    jobs_apply_now: "Postulez maintenant",
    jobs_load_more: "Charger plus",
    jobs_empty_title: "Nous n'avons pas trouvé de postes",
    jobs_empty_desc: "Essayez d'ajuster vos filtres de recherche ou envoyez-nous votre CV pour de futures opportunités.",
    jobs_send_cv: "ENVOYER UN CV",
    jobs_salary: "Salaire",
    jobs_save: "Sauvegarder",
    jobs_share: "Partager",
    jobs_link_copied: "Lien copié",
    jobs_dialog_apply_title: "Postuler au poste",
    jobs_dialog_apply_vacancy: "Position",
    jobs_field_name: "Nom",
    jobs_field_email: "E-mail",
    jobs_field_whatsapp: "WhatsApp",
    jobs_field_cvurl: "URL du CV (Drive/Dropbox)",
    jobs_field_message_optional: "Message (facultatif)",
    jobs_send: "Envoyer",
    jobs_application_sent: "Candidature envoyée",
    jobs_application_error: "Erreur lors de l'envoi de la candidature",
    // Jobs Benefits & Culture
    jobs_benefits_title_prefix: "PLUS QU'UN",
    jobs_benefits_title_highlight: "EMPLOI",
    jobs_benefits_subtitle: "Une expérience de croissance personnelle et professionnelle unique",
    jobs_benefit_international_title: "Expérience internationale",
    jobs_benefit_international_desc: "Possibilités de travail au Canada, en Australie et dans d'autres destinations",
    jobs_benefit_professional_title: "Développement professionnel",
    jobs_benefit_professional_desc: "Formation continue et certifications internationales",
    jobs_benefit_multicultural_title: "Équipe multiculturelle",
    jobs_benefit_multicultural_desc: "Travailler avec des personnes de différents pays et cultures",
    jobs_benefit_medical_title: "Assurance médicale premium",
    jobs_benefit_medical_desc: "Une couverture complète pour vous et votre famille",
    jobs_benefit_bonus_title: "Primes de performance",
    jobs_benefit_bonus_desc: "Récompenses supplémentaires en fonction de vos performances",
    jobs_benefit_flex_title: "Flexibilité du travail",
    jobs_benefit_flex_desc: "Horaires flexibles et options de travail à distance",
    jobs_culture_title_prefix: "NOTRE",
    jobs_culture_title_highlight: "VALEURS",
    jobs_culture_subtitle: "Ce qui nous rend unique en tant qu'équipe et entreprise",
    jobs_value_passion_title: "Passion pour transformer des vies",
    jobs_value_passion_desc: "Nous croyons au pouvoir de l'éducation internationale pour changer les voies et créer des opportunités. Nous sommes motivés pour aider chaque personne à atteindre son plein potentiel.",
    jobs_value_team_title: "Travail d'équipe",
    jobs_value_team_desc: "Nous collaborons, nous soutenons les uns les autres et grandissons ensemble. Le succès d'une personne est le succès de tous. Nous favorisons une communication ouverte et le respect mutuel.",
    jobs_value_excellence_title: "Excellence et Qualité",
    jobs_value_excellence_desc: "Nous visons l'excellence dans les moindres détails, de la première consultation au suivi. Nous visons toujours à dépasser les attentes.",
    jobs_value_diversity_title: "Diversité et inclusion",
    jobs_value_diversity_desc: "Nous valorisons et respectons les différences culturelles et personnelles. Nous pensons que la diversité nous enrichit et nous rend meilleurs.",
    jobs_value_integrity_title: "Intégrité",
    jobs_value_integrity_desc: "Nous agissons avec honnêteté et éthique professionnelle dans chaque processus. La confiance de nos étudiants et collègues est notre priorité.",
    jobs_value_innovation_title: "Innovation",
    jobs_value_innovation_desc: "Nous conduisons de nouvelles idées pour améliorer l'expérience de nos étudiants et collègues. Nous nous adaptons au changement et recherchons des solutions créatives.",
    jobs_cta_title_prefix: "PRÊT À",
    jobs_cta_title_highlight: "COMMENCER?",
    jobs_cta_paragraph: "Rejoignez une équipe qui transforme des vies et construit l'avenir. Votre prochaine aventure professionnelle vous attend.",
    jobs_step_simple_process: "Processus de candidature simple",
    jobs_step_48h_response: "Réponse sous 48 heures",
    jobs_step_team_interviews: "Entretiens avec l'équipe",
    jobs_step_onboarding: "Intégration personnalisée",
    jobs_btn_send_my_cv: "ENVOYER MON CV",
    jobs_btn_contact_hr: "CONTACTER LES RH",
    
    // Australia page (ES)
    auss_meta_title: "LET - Viva Australie | Boostez votre avenir en Australie",
    auss_whatsapp_text: "Salut! J'aimerais en savoir plus sur vos services",
    auss_whatsapp_tooltip: "Discutez avec nous !",
    auss_hero_title: "Boostez votre avenir en Australie",
    auss_hero_tagline: "Une alliance par et pour les talents mexicains",
    auss_hero_subtitle: "Découvrez comment LET et VIVA Australia travaillent ensemble pour ouvrir la porte à des expériences de travail inoubliables dans un pays plein d'opportunités.",
    auss_cta_more: "Je veux en savoir plus !",
    
    auss_alliance_badge: "Alliance stratégique formelle",
    auss_alliance_title: "Une alliance par et pour les talents mexicains",
    auss_alliance_subtitle: "Cette collaboration naît de notre objectif commun d'offrir des solutions globales aux jeunes Mexicains à la recherche d'une expérience professionnelle à l'étranger, avec un soutien fiable, des conseils personnalisés et un accompagnement continu.",
    auss_alliance_h3: "Pourquoi cette alliance ?",
    auss_alliance_h4: "Un pont entre le Mexique et l'Australie",
    auss_alliance_p1_bold: "Prestations complémentaires :",
    auss_alliance_p1_rest: "LET offre une solide expertise en matière de conseil et de préparation pour des destinations comme l'Australie, tandis que Viva Australia renforce le volet juridique et d'immigration avec une expérience spécialisée en matière de visas, de réglementations et de représentation auprès des autorités australiennes.",
    auss_alliance_p2_bold: "Conseils tout-en-un :",
    auss_alliance_p2_rest: "Grâce à cette alliance, jeunes diplômés, futurs diplômés et professionnels peuvent accéder à une solution 100% intégrée.",
    auss_alliance_p3_bold: "Confiance et accompagnement professionnel :",
    auss_alliance_p3_rest: "Les deux organisations détiennent des accréditations clés telles que MARA, MIA, QEAC, CRICOS et des alliances avec des universités et institutions reconnues, garantissant que chaque processus est géré de manière transparente et conforme à la loi en vigueur.",
    auss_alliance_p4_bold: "Couverture régionale et proximité :",
    auss_alliance_p4_rest: "Présente en Amérique latine et en Australie, cette alliance offre un accompagnement étroit avant et pendant le voyage, avec des équipes locales qui comprennent à la fois le contexte d'origine et le pays de destination.",
    auss_youtube_alt: "Viva Australie et LET Alliance",
    
    auss_benefits_badge: "Objectifs de l'Alliance",
    auss_benefits_title: "Avantages exclusifs de notre alliance",
    auss_benefits_subtitle: "Chaque avantage est conçu pour que votre projet en Australie devienne plus qu'un rêve : une réalité stratégique et réussie qui propulse les talents mexicains vers un avenir mondial.",
    auss_benefit1_title: "Votre réussite, nos conseils",
    auss_benefit1_desc: "Bénéficiez de conseils experts et personnalisés qui définissent votre développement professionnel en Australie.",
    auss_benefit2_title: "Voyagez en toute sérénité",
    auss_benefit2_desc: "Bénéficiez d'un accompagnement à 360°, des visas à l'adaptation, sans soucis.",
    auss_benefit3_title: "Boostez votre carrière mondiale",
    auss_benefit3_desc: "Accédez à d'innombrables opportunités professionnelles dans l'une des économies les plus dynamiques au monde.",
    auss_benefit4_title: "Le pouvoir de deux experts",
    auss_benefit4_desc: "L'expérience combinée de LET et VIVA Australia, deux agences de confiance internationale.",
    
    auss_partners_badge: "Apprenez à nous connaître",
    auss_partners_title: "Qui nous sommes",
    auss_partners_subtitle: "Deux organisations de premier plan unies par un objectif commun : réaliser les rêves professionnels des jeunes Mexicains en Australie.",
    auss_partner_let_slogan: "\\\"Nous sommes votre moyen d'atteindre le monde\\\"",
    auss_partner_let_desc: "Grupo LET est une entreprise mexico-canadienne spécialisée dans le tourisme éducatif et de loisirs. Fondée à Toluca en 2005. Nous avons participé à plus de 15 347 expériences et rêves devenus réalité, en maintenant la qualité et le professionnalisme de nos services.\\n\\nEngagement : Renforcer notre objectif en tant qu'institution en ouvrant des voies mondiales grâce à la formation, l'orientation et la mobilité internationale.",
    auss_partner_viva_slogan: "\\\"Faites de votre projet de vie en Australie une réalité\\\"",
    auss_partner_viva_desc: "VIVA Australia est une agence australienne présente en Amérique latine et en Australie depuis 1999, spécialisée dans la migration et l'éducation. Nous proposons des conseils professionnels de haut niveau à ceux qui recherchent une expérience académique ou professionnelle en Australie.\\n\\nEngagement : Guider les étudiants latins vers des programmes d'études de qualité et aider les professionnels à exercer leur carrière dans le pays.",
    
    auss_slider1_title: "Votre avenir en Australie commence aujourd'hui",
    auss_slider1_desc: "Ne manquez pas l'occasion de découvrir comment notre alliance LET-Viva Australia peut ouvrir les portes d'expériences transformatrices. Notre équipe est prête à vous fournir les conseils dont vous avez besoin pour faire votre premier pas vers un avenir australien.",
    auss_slider1_cta: "Planifier des conseils gratuits",
    auss_slider2_title: "Australie : là où vos rêves professionnels deviennent réalité.",
    auss_slider2_desc: "Avec le soutien de LET et Viva Australia, votre ambition de grandir et de briller sur le marché du travail international trouve sa voie. Ce pays offre le cadre idéal pour bâtir la carrière que vous avez toujours imaginée, entouré d'innovations et d'opportunités sans précédent.",
    auss_slider2_cta: "Explorer les opportunités",
    
    auss_target_badge: "Pour toi",
    auss_target_title: "Est-ce votre opportunité ?",
    auss_target_h2: "À qui s'adresse cette opportunité ?",
    auss_target_h5: "Ce programme est fait pour vous si :",
    auss_target_item1: "Vous souhaitez effectuer un stage professionnel en Australie",
    auss_target_item2: "Vous devez améliorer votre anglais",
    auss_target_item3: "Vous souhaitez développer des compétences globales dans votre domaine professionnel",
    auss_target_item4: "Vous voulez transformer votre vie",
    auss_target_item5: "Vous souhaitez une expérience de travail internationale",
    auss_target_item6: "Vous souhaitez découvrir de nouvelles cultures",
    
    auss_contact_title: "Start your Australian adventure!",
    auss_contact_subtitle: "Fill out the form and receive free personalized advice to plan your future in Australia.",
  },
  FR: {
    nav_nosotros: "À PROPOS",
    nav_destinos: "DESTINATIONS",
    nav_servicios: "SERVICES",
    nav_recursos: "RESSOURCES",
    nav_eventos: "ÉVÉNEMENTS",
    nav_consulta: "Consultation gratuite",
    nav_promociones: "PROMOTIONS",
    // Add more French translations here as needed
  }
};

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>('ES')

  useEffect(() => {
    try {
      // If middleware placed a cookie `let-lang`, prefer it for this load and then remove it
      let cookieLang: Lang | null = null
      try {
        const m = document.cookie.match(/(?:^|; )let-lang=([^;]+)/)
        cookieLang = (m && m[1]) as Lang | null
      } catch {}

      if (cookieLang === 'ES' || cookieLang === 'EN' || cookieLang === 'FR') {
        setLangState(cookieLang)
        document.documentElement.lang = cookieLang === 'EN' ? 'en' : cookieLang === 'FR' ? 'fr' : 'es'
        // remove the temporary cookie so it doesn't persist beyond this page
        try {
          document.cookie = 'let-lang=; Path=/; Max-Age=0; SameSite=Lax'
        } catch {}
        return
      }

      const saved = localStorage.getItem('let-lang') as Lang | null
      if (saved === 'ES' || saved === 'EN' || saved === 'FR') {
        setLangState(saved)
        document.documentElement.lang = saved === 'EN' ? 'en' : saved === 'FR' ? 'fr' : 'es'
      } else {
        document.documentElement.lang = document.documentElement.lang || 'es'
      }
    } catch {}
  }, [])

  const setLang = (l: Lang) => {
    setLangState(l)
    try {
      localStorage.setItem('let-lang', l)
      document.documentElement.lang = l === 'EN' ? 'en' : l === 'FR' ? 'fr' : 'es'
      window.dispatchEvent(new CustomEvent('let-lang-change', { detail: l }))
    } catch {}
  }

  const t = (key: string) => DICTS[lang]?.[key] ?? DICTS['ES']?.[key] ?? key

  const value = useMemo(() => ({ lang, setLang, t }), [lang])

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  )
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}
